import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import ReactDOM from "react-dom";
import { Redirect, useHistory, useParams } from "react-router-dom";
import PlayerScoreAlert from "./_components/player_score_alert";
import PlayerResumeDialog from "./_components/player_resume_dialog";
import PlayerShortcutsDialog from "./_components/player_shortcuts_dialog";
import {
  fetchSpriteVtt,
  findSpriteCue,
  parseSpriteVtt,
  proxyVideoUrl,
  readSpriteFields,
  spriteDisplaySize,
  spriteUrlForCue,
  type SpriteCue,
  type SpriteFrame,
  type SpriteSheetMap,
} from "./_components/sprite-preview";
import { VideoJsBridge, useVideoJs } from "./VideoJsBridge";
import type { VideoJsPlayer } from "@/types/videojs-player";
import { isBackKey, mediaAction } from "@/lib/tv-keys";
import { focusEl, isShown } from "@/lib/tv-navigation";

import aspectRatioIcon from "@public/img/player-aspect-ratio.svg";
import backIcon from "@public/img/arrow-left.svg";
import chevronDownIcon from "@public/img/chevron-down.svg";
import eyeIcon from "@public/img/eye.svg";
import episodeIcon from "@public/img/player-episode.svg";
import forwardIcon from "@public/img/player-forward.svg";
import fullscreenExitIcon from "@public/img/player-fullscreen-exit.svg";
import fullscreenIcon from "@public/img/player-fullscreen.svg";
import lockIcon from "@public/img/player-lock.svg";
import pauseIcon from "@public/img/player-pause.svg";
import playIcon from "@public/img/player-play.svg";
import rulerIcon from "@public/img/player-ruler.svg";
import redoIcon from "@public/img/player-redo.svg";
import qualityIcon from "@public/img/player-setting.svg";
import speedIcon from "@public/img/player-speed.svg";
import subtitleIcon from "@public/img/player-subtitle.svg";
import bgColorIcon from "@public/img/player-bgcolor.svg";
import colorIcon from "@public/img/player-color.svg";
import fontIcon from "@public/img/player-font.svg";
import volumeCrossIcon from "@public/img/player-volume-cross.svg";
import volumeIcon from "@public/img/player-volume.svg";
import volumeLoudIcon from "@public/img/player-volume-loud.svg";
import volumeSmallIcon from "@public/img/player-volume-small.svg";
import loadingGif from "@public/img/loading.gif";
import settingsIcon from "@public/img/setting.svg";
import closeIcon from "@public/img/close.svg";

import type {
  PlayerData as PlayerApiData,
  PlayerDownloadOption,
  PlayerSubtitle,
  WatchListItemRequest,
} from "@/entry/server/movie";
import { videoRepo } from "@/repo/video-repo";
import { ASSETS_BASE_URL } from "@/utils/constants";
import { encryptAes } from "@/utils/aes";
import { AppContextType, WonmovContext } from "@/context/app-context";
import { userRepo } from "@/repo/user-repo";
import { useQueryParams, getQueryParam } from "@/hooks/useQueryParams";
import { isPlayerEnded, safePlay } from "@/lib/videojs-setup";
import "@/styles/videojs-overrides.css";
import "@/styles/player-page.css";
import "@/styles/player-settings-drawer.css";
import "@/styles/player-episodes-drawer.css";

// ---------------------------------------------------------------------------
// ثابت‌ها و تایپ‌ها
// ---------------------------------------------------------------------------

const CONTROLS_HIDE_DELAY_MS = 5000;
const PLAYER_CONTROLS_ZONE_SELECTOR = "[data-player-controls-zone]";
const ICON_PX = 36;

type SearchParamsShape = {
  season: number;
  episode: number;
  quality: string;
  type: "farsi_dubbed" | "with_subtitle" | "without_subtitle";
};

type CustomControlsProps = {
  movieId: number;
  currentQualityCode: string;
  currentEpisode: number;
  currentSeason: number;
  onEpisodeSelect: (season: number, episode: number) => void;
  playbackOptions: PlayerDownloadOption[];
  playerData: PlayerApiData;
  streamUrl: string;
  playbackStreamType: SearchParamsShape["type"];
  /** وقتی هم دوبله و هم نسخهٔ با زیرنویس در منابع باشد. */
  canToggleDubSubtitle: boolean;
  onToggleDubSubtitle: () => void;
  onBack: () => void;
  onStreamChange: (url: string | null) => void;
  /** زیرنویس‌های سرور + فایل‌های محلی کاربر (برای ترک و منو). */
  subtitlesForTracks: PlayerSubtitle[];
  onUserSubtitleImported: (sub: PlayerSubtitle) => void;
};

type MimeType = "application/x-mpegurl" | "video/mp4";

// ---------------------------------------------------------------------------
// توابع کمکی (بدون state)
// ---------------------------------------------------------------------------

function formatPlayerTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

function durationOrZero(p: VideoJsPlayer): number {
  const du = p.duration();
  return typeof du === "number" && Number.isFinite(du) ? du : 0;
}

/** ترک‌های زیرنویس به همان ترتیب `addRemoteTextTrack` در VideoJsBridge (برای تطبیق `sub-0` …). */
function collectSubtitleTracks(list: TextTrackList | TextTrack[], out: TextTrack[]) {
  for (let i = 0; i < list.length; i += 1) {
    const t = list[i];
    if (t && t.kind === "subtitles") {
      out.push(t);
    }
  }
}

function getOrderedSubtitleTracks(p: VideoJsPlayer): TextTrack[] {
  const out: TextTrack[] = [];
  const remote = p.remoteTextTracks();
  if (remote && remote.length > 0) {
    collectSubtitleTracks(remote as TextTrackList, out);
  }
  if (out.length > 0) {
    return out;
  }
  collectSubtitleTracks(p.textTracks() as TextTrackList, out);
  return out;
}

function setTextTrackMode(track: TextTrack, mode: TextTrack["mode"]) {
  // فقط وقتی واقعاً تغییر کرده مقداردهی می‌کنیم تا رویداد change اضافی صادر نشود.
  if (track.mode !== mode) {
    track.mode = mode;
  }
}

function applyCaptionSelection(p: VideoJsPlayer, value: string) {
  const tracks = getOrderedSubtitleTracks(p);
  const subIdx = /^sub-(\d+)$/.exec(value);
  const targetIndex = subIdx ? Number(subIdx[1]) : NaN;

  for (let i = 0; i < tracks.length; i += 1) {
    const track = tracks[i];
    if (value === "off") {
      setTextTrackMode(track, "disabled");
      continue;
    }
    const langMatch = track.language === value;
    const indexMatch = Number.isFinite(targetIndex) && i === targetIndex;
    setTextTrackMode(track, langMatch || indexMatch ? "showing" : "hidden");
  }
}

function mimeFromFormat(fmt: PlayerDownloadOption["format"] | undefined): MimeType {
  return fmt === "m3u8" ? "application/x-mpegurl" : "video/mp4";
}

function resolvePlayerMediaUrl(rawUrl: string): string {
  var url = rawUrl ? String(rawUrl) : "";
  if (!url) {
    return "";
  }
  if (url.indexOf("http") === 0 || url.indexOf("blob:") === 0) {
    return url;
  }
  return ASSETS_BASE_URL + url.replace("files/", "");
}

function sliderThumbStyle(percent: number, offsetPx: number): React.CSSProperties {
  return {
    left: percent + "%",
    marginLeft: -offsetPx,
  };
}

/**
 * نسبت ۰..۱ موقعیت کلیک/لمس روی اسلایدر را برمی‌گرداند.
 * در مرورگرهای قدیمی (Chrome 20 / WebView)، input range با کلیک به موقعیت کلیک
 * نمی‌پرد؛ این تابع مقدار دقیق را از روی هندسهٔ خود input محاسبه می‌کند.
 */
function sliderRatioFromClientX(el: HTMLElement, clientX: number): number {
  const rect = el.getBoundingClientRect();
  if (!rect.width) return 0;
  let ratio = (clientX - rect.left) / rect.width;
  if (ratio < 0) ratio = 0;
  if (ratio > 1) ratio = 1;
  return ratio;
}

function clientXFromTouch(
  e: React.TouchEvent<HTMLElement>
): number | null {
  const t = e.touches && e.touches.length ? e.touches[0] : null;
  return t ? t.clientX : null;
}

function sanitizedTitle(raw: string): string {
  return raw.replace(/[/\\?%*:|"<>#\x00-\x1f]/g, "_").trim() || "wonmov";
}

/** اگر true باشد، میانبر پلیر اجرا نمی‌شود (ورودی/IME/دکمه‌های فوکوس‌داده). */
function suppressPlayerKeyboard(ev: KeyboardEvent): boolean {
  if (typeof document !== "undefined" && document.querySelector("[data-player-shortcuts-dialog]")) {
    return true;
  }
  if ((ev as KeyboardEvent & { isComposing?: boolean }).isComposing) {
    return true;
  }
  let el = ev.target;
  /** نفوود به عنصر فوکوس‌شدهٔ داخل سایه‌اخت (به‌درست ناقص؛ برای اکثر بازیکن‌ها کافی است) */
  for (let g = 0; g < 4 && el instanceof Element; g += 1) {
    const sr = el.shadowRoot;
    if (
      sr &&
      sr.activeElement instanceof HTMLElement
    ) {
      el = sr.activeElement;
    } else break;
  }

  if (!(el instanceof HTMLElement)) return false;

  if (el.tagName === "VIDEO") return false;

  if (el.getAttribute("data-player-time-slider") != null) return false;
  if (el.closest("[data-player-time-slider]")) return false;

  const formLike = el.closest(
    'input, textarea, select, button, [contenteditable="true"], [role="combobox"], [role="textbox"], [role="listbox"]'
  );

  /** روی هر input (منجمله اسلایدر ولوم/) کیفیت باز، میانبر پلیر رهاشود */
  if (formLike) return true;

  return false;
}

const SEEK_STEP_ARROW_S = 5;
const SEEK_STEP_MAIN_S = 10;
const SEEK_STEP_SHIFT_ARROW_S = 30;
const VOLUME_KEYBOARD_STEP = 0.08;
const RATE_KEYBOARD_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;
const SUBTITLE_BG_COLORS = [
  {
    label: "بی رنگ",
    value: "sub-transparent-bg",
  },
  {
    label: "تیره کم رنگ",
    value: "sub-light-dark-bg",
  },
  {
    label: "مشکی",
    value: "sub-dark-bg",
  },
]
const SUBTITLE_FONT_FAMILIES = [
  {
    label: "دانا",
    value: "sub-font-dana",
  },
  {
    label: "وزیر متن",
    value: "sub-font-vazir",
  },
  {
    label: "ایران سنس",
    value: "sub-font-iransans",
  },
]
const SUBTITLE_TEXT_COLORS = [
  {
    label: "سفید",
    value: "sub-text-white",
  },
  {
    label: "زرد",
    value: "sub-text-yellow",
  },
  {
    label: "آبی",
    value: "sub-text-blue",
  },
]
const SUBTITLE_FONT_WEIGHTS = [
  {
    label: "نازک",
    value: "sub-font-light",
  },
  {
    label: "معمولی",
    value: "sub-font-regular",
  },
  {
    label: "درشت",
    value: "sub-font-bold",
  },
  {
    label: "خیلی درشت",
    value: "sub-font-extrabold",
  },
]

const SUBTITLE_TEXT_SIZES = [
  {
    label: "کوچک",
    value: 14,
  },
  {
    label: "متوسط",
    value: 16,
  },
  {
    label: "بزرگ",
    value: 20,
  },
]

const SUBTITLE_MARGINS = [
  {
    label: "کم",
    value: 0,
  },
  {
    label: "متوسط",
    value: 20,
  },
  {
    label: "زیاد",
    value: 40,
  },
]

const SUBTITLE_TEXT_SIZE_VALUES = SUBTITLE_TEXT_SIZES.map(function (item) {
  return item.value;
})

const SUBTITLE_MARGIN_VALUES = SUBTITLE_MARGINS.map(function (item) {
  return item.value;
})

const PLAYER_UI_SETTINGS_STORAGE_KEY = "wonmov-player-ui-settings";
const PLAYBACK_PROGRESS_AES_KEY = "GHjki78pFncYreKj";
const PLAYBACK_PROGRESS_SAVE_INTERVAL_MS = 30_000;
var playingTime = 0;

type PlayerUiSettingsV1 = {
  v: 1;
  playbackSpeed: number;
  subtitleBgColor: string;
  subtitleTextColor: string;
  subtitleFontFamily: string;
  subtitleFontWeight: string;
  subtitleTextSize: number;
  subtitleMargin: number;
};

const SUBTITLE_BG_VALUE_SET = new Set(SUBTITLE_BG_COLORS.map((c) => c.value));
const SUBTITLE_TEXT_COLOR_VALUE_SET = new Set(SUBTITLE_TEXT_COLORS.map((c) => c.value));
const SUBTITLE_FONT_FAMILY_VALUE_SET = new Set(SUBTITLE_FONT_FAMILIES.map((f) => f.value));
const SUBTITLE_FONT_WEIGHT_VALUE_SET = new Set(SUBTITLE_FONT_WEIGHTS.map((w) => w.value));

function defaultPlayerUiSettings(): PlayerUiSettingsV1 {
  return {
    v: 1,
    playbackSpeed: 1,
    subtitleBgColor: "sub-dark-bg",
    subtitleTextColor: "sub-text-white",
    subtitleFontFamily: "sub-font-dana",
    subtitleFontWeight: "sub-font-regular",
    subtitleTextSize: 16,
    subtitleMargin: 0,
  };
}

function normalizePlaybackSpeed(n: unknown): number {
  const steps = [...RATE_KEYBOARD_STEPS] as number[];
  if (typeof n !== "number" || !Number.isFinite(n)) return 1;
  if (steps.includes(n)) return n;
  return steps.reduce((best, cur) =>
    Math.abs(cur - n) < Math.abs(best - n) ? cur : best
  );
}

function snapToNearestStep(n: number, steps: number[]): number {
  if (!steps.length) return n;
  return steps.reduce(function (best, cur) {
    return Math.abs(cur - n) < Math.abs(best - n) ? cur : best;
  });
}

function normalizeSubtitleTextSize(n: unknown): number {
  const d = defaultPlayerUiSettings();
  if (typeof n !== "number" || !Number.isFinite(n)) return d.subtitleTextSize;
  return snapToNearestStep(Math.round(n), SUBTITLE_TEXT_SIZE_VALUES);
}

function normalizeSubtitleMargin(n: unknown): number {
  const d = defaultPlayerUiSettings();
  if (typeof n !== "number" || !Number.isFinite(n)) return d.subtitleMargin;
  return snapToNearestStep(Math.round(n), SUBTITLE_MARGIN_VALUES);
}

function parsePlayerUiSettings(raw: string | null): PlayerUiSettingsV1 | null {
  if (raw == null || raw === "") return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const r = parsed as Record<string, unknown>;
    const d = defaultPlayerUiSettings();
    const bg =
      typeof r.subtitleBgColor === "string" && SUBTITLE_BG_VALUE_SET.has(r.subtitleBgColor)
        ? r.subtitleBgColor
        : d.subtitleBgColor;
    const textColor =
      typeof r.subtitleTextColor === "string" &&
      SUBTITLE_TEXT_COLOR_VALUE_SET.has(r.subtitleTextColor)
        ? r.subtitleTextColor
        : d.subtitleTextColor;
    const fontFamily =
      typeof r.subtitleFontFamily === "string" &&
      SUBTITLE_FONT_FAMILY_VALUE_SET.has(r.subtitleFontFamily)
        ? r.subtitleFontFamily
        : d.subtitleFontFamily;
    const fontWeight =
      typeof r.subtitleFontWeight === "string" &&
      SUBTITLE_FONT_WEIGHT_VALUE_SET.has(r.subtitleFontWeight)
        ? r.subtitleFontWeight
        : d.subtitleFontWeight;
    let textSize = normalizeSubtitleTextSize(r.subtitleTextSize);
    let margin = normalizeSubtitleMargin(r.subtitleMargin);
    return {
      v: 1,
      playbackSpeed: normalizePlaybackSpeed(r.playbackSpeed),
      subtitleBgColor: bg,
      subtitleTextColor: textColor,
      subtitleFontFamily: fontFamily,
      subtitleFontWeight: fontWeight,
      subtitleTextSize: textSize,
      subtitleMargin: margin,
    };
  } catch {
    return null;
  }
}

function readPlayerUiSettingsFromLocalStorage(): PlayerUiSettingsV1 | null {
  if (typeof window === "undefined") return null;
  return parsePlayerUiSettings(localStorage.getItem(PLAYER_UI_SETTINGS_STORAGE_KEY));
}

function getVjsTextTrackDisplayEl(p: VideoJsPlayer | null): HTMLElement | null {
  if (!p || p.isDisposed()) return null;
  const root = typeof p.el === "function" ? p.el() : null;
  if (!(root instanceof Element)) return null;
  const el = root.querySelector(".vjs-text-track-display");
  return el instanceof HTMLElement ? el : null;
}

function applyPlayerUiToPlayerInstance(p: VideoJsPlayer, s: PlayerUiSettingsV1): void {
  if (p.isDisposed()) return;
  try {
    p.playbackRate(s.playbackSpeed);
  } catch {
    /* no-op */
  }
  const el = getVjsTextTrackDisplayEl(p);
  if (!el) return;
  SUBTITLE_BG_COLORS.forEach((c) => el.classList.remove(c.value));
  el.classList.add(s.subtitleBgColor);
  SUBTITLE_TEXT_COLORS.forEach((c) => el.classList.remove(c.value));
  el.classList.add(s.subtitleTextColor);
  SUBTITLE_FONT_WEIGHTS.forEach((w) => el.classList.remove(w.value));
  el.classList.add(s.subtitleFontWeight);
  SUBTITLE_FONT_FAMILIES.forEach((f) => el.classList.remove(f.value));
  el.classList.add(s.subtitleFontFamily);
  SUBTITLE_TEXT_SIZE_VALUES.forEach(function (size) {
    el.classList.remove("text-size-" + size);
  });
  el.classList.add("text-size-" + s.subtitleTextSize);
}

var SUBTITLE_MARGIN_PX_PER_UNIT = 5;
var SUBTITLE_BASE_CONTROLS_OFFSET_PX = 40;
var TAP_MOVE_PX = 12;
var VOLUME_HOLD_INTERVAL_MS = 90;
var HUD_HOLD_MS = 800;
var HUD_EXIT_MS = 260;
var NEXT_EPISODE_WINDOW_S = 60;
var NEXT_EPISODE_VISIBLE_MS = 15000;

var ASPECT_RATIO_OPTIONS = [
  { className: "ratio-cover", label: "پوشش" },
  { className: "ratio-contain", label: "جا شدن" },
  { className: "ratio-fill", label: "کشیده" },
  { className: "ratio-none", label: "اندازهٔ اصلی" },
];

function subtitleUserMarginPx(units: number): number {
  var n = units;
  if (typeof n !== "number" || !isFinite(n)) n = 0;
  if (n < 0) n = 0;
  if (n > 80) n = 80;
  return Math.round(n * SUBTITLE_MARGIN_PX_PER_UNIT);
}

function setSubtitleTranslate(el: HTMLElement, shiftPx: number) {
  var px = Math.round(shiftPx);
  if (px < 0) px = 0;
  var value = "translateY(-" + px + "px)";
  var style = el.style as CSSStyleDeclaration & { webkitTransform: string };
  style.webkitTransform = value;
  el.style.transform = value;
}

function formatFaInt(n: number): string {
  var digits = "۰۱۲۳۴۵۶۷۸۹";
  var raw = String(Math.floor(Math.abs(n)));
  var out = "";
  var i = 0;
  for (i = 0; i < raw.length; i += 1) {
    var code = raw.charCodeAt(i);
    if (code >= 48 && code <= 57) out += digits.charAt(code - 48);
    else out += raw.charAt(i);
  }
  return out;
}

function formatSeriesPosition(season: number, episode: number): string {
  var parts: string[] = [];
  if (season > 0) parts.push("فصل " + formatFaInt(season));
  if (episode > 0) parts.push("قسمت " + formatFaInt(episode));
  return parts.join(" ");
}

function volumeIconForLevel(muted: boolean, volume: number): string {
  if (muted || volume <= 0) return volumeCrossIcon;
  if (volume > 0.5) return volumeLoudIcon;
  if (volume > 0.25) return volumeSmallIcon;
  return volumeIcon;
}

function isUiElement(target: EventTarget | null): target is Element {
  return target instanceof Element;
}

function isInControlsZone(target: EventTarget | null): boolean {
  return isUiElement(target) && Boolean(target.closest(PLAYER_CONTROLS_ZONE_SELECTOR));
}

function isDirectVideoTarget(target: EventTarget | null): boolean {
  return isUiElement(target) && Boolean(target.closest("video, .vjs-tech"));
}

function isPlaySurface(target: EventTarget | null): boolean {
  if (!isUiElement(target)) return false;
  if (isInControlsZone(target)) return false;
  if (target.closest("[data-player-ignore-toggle]")) return false;
  if (target.closest("[data-player-shortcuts-dialog]")) return false;
  if (target.closest("[data-player-chrome]") === target) return true;
  if (isDirectVideoTarget(target)) return true;
  return false;
}

function findNextEpisodeItem(
  items: EpisodeListItem[],
  season: number,
  episode: number
): EpisodeListItem | null {
  var i = 0;
  for (i = 0; i < items.length; i += 1) {
    var item = items[i];
    if (item.season > season || (item.season === season && item.episode > episode)) {
      return item;
    }
  }
  return null;
}

type PlayerHudKind = "time" | "volume" | "text";

type PlayerHudState = {
  kind: PlayerHudKind;
  text: string;
  volume: number;
  muted: boolean;
  phase: "in" | "out";
  anim: number;
};

function PlayerControlTooltip(props: {
  label: string;
  hidden?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  var cls = "player-tooltip";
  if (props.className) cls += " " + props.className;
  return (
    <span className={cls}>
      {props.children}
      {props.hidden ? null : (
        <span className="player-tooltip__label" dir="rtl">
          {props.label}
        </span>
      )}
    </span>
  );
}

var KEY_CODE_TO_PHYSICAL: { [code: number]: string } = {
  13: "Enter",
  27: "Escape",
  32: "Space",
  35: "End",
  36: "Home",
  37: "ArrowLeft",
  38: "ArrowUp",
  39: "ArrowRight",
  40: "ArrowDown",
  48: "Digit0",
  49: "Digit1",
  50: "Digit2",
  51: "Digit3",
  52: "Digit4",
  53: "Digit5",
  54: "Digit6",
  55: "Digit7",
  56: "Digit8",
  57: "Digit9",
  65: "KeyA",
  66: "KeyB",
  67: "KeyC",
  70: "KeyF",
  73: "KeyI",
  74: "KeyJ",
  75: "KeyK",
  76: "KeyL",
  77: "KeyM",
  80: "KeyP",
  81: "KeyQ",
  96: "Numpad0",
  97: "Numpad1",
  98: "Numpad2",
  99: "Numpad3",
  100: "Numpad4",
  101: "Numpad5",
  102: "Numpad6",
  103: "Numpad7",
  104: "Numpad8",
  105: "Numpad9",
  188: "Comma",
  190: "Period",
  219: "BracketLeft",
  221: "BracketRight",
};

function physicalKey(ev: KeyboardEvent): string {
  var withCode = ev as KeyboardEvent & { code?: string };
  if (withCode.code) return withCode.code;
  var which = ev.keyCode || (ev as KeyboardEvent & { which?: number }).which || 0;
  return KEY_CODE_TO_PHYSICAL[which] || "";
}

function navigationKey(ev: KeyboardEvent): string {
  var key = ev.key;
  if (key === " " || key === "Spacebar") return " ";
  if (
    key === "ArrowLeft" ||
    key === "ArrowRight" ||
    key === "ArrowUp" ||
    key === "ArrowDown" ||
    key === "Home" ||
    key === "End" ||
    key === "Enter" ||
    key === "Escape"
  ) {
    return key;
  }
  var which = ev.keyCode || 0;
  if (which === 32) return " ";
  if (which === 13) return "Enter";
  if (which === 27) return "Escape";
  if (which === 36) return "Home";
  if (which === 35) return "End";
  if (which === 37) return "ArrowLeft";
  if (which === 38) return "ArrowUp";
  if (which === 39) return "ArrowRight";
  if (which === 40) return "ArrowDown";
  return "";
}

type TvLayerBridge = {
  overlayVisible: boolean;
  layerOpen: boolean;
  closeTopLayer: () => boolean;
};

function isTimeSliderFocused(el: HTMLElement | null): boolean {
  if (!el || !el.closest) return false;
  if (el.getAttribute("data-player-time-slider") != null) return true;
  if (el.closest("[data-player-time-slider]")) return true;
  return false;
}

function focusPlayerPlayButton(): void {
  var btn = document.querySelector("[data-tv-play]");
  if (!btn) return;
  focusEl(btn as HTMLElement, false);
}

function isInsidePlayerControls(el: HTMLElement | null): boolean {
  if (!el || !el.closest) return false;
  return el.closest("[data-player-controls-zone]") != null;
}

function digitFromPhysical(code: string): number | null {
  if (code.indexOf("Digit") === 0 && code.length === 6) {
    var n = Number(code.charAt(5));
    return n >= 0 && n <= 9 ? n : null;
  }
  if (code.indexOf("Numpad") === 0 && code.length === 7) {
    var pad = Number(code.charAt(6));
    return pad >= 0 && pad <= 9 ? pad : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// هوک همگام‌سازی کنترل‌های سفارشی با Video.js
// ---------------------------------------------------------------------------

function useVideoJsChrome(
  player: VideoJsPlayer | null,
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  onPeekOverlayHidden?: () => void,
  interactionLockedRef?: React.MutableRefObject<boolean>
) {
  const [paused, setPaused] = useState(true);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [peekOverlay, setPeekOverlay] = useState(false);
  const [buffering, setBuffering] = useState(false);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdUntilRef = useRef(0);
  const scrubWasPlayingRef = useRef(false);
  const peekOverlayRef = useRef(false);
  const onHiddenRef = useRef(onPeekOverlayHidden);

  peekOverlayRef.current = peekOverlay;
  onHiddenRef.current = onPeekOverlayHidden;

  const setPeekOverlaySynced = useCallback((visible: boolean) => {
    peekOverlayRef.current = visible;
    setPeekOverlay(visible);
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current != null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const hidePeekOverlay = useCallback(() => {
    setPeekOverlaySynced(false);
    onHiddenRef.current?.();
  }, [setPeekOverlaySynced]);

  const controlsLockedNow = useCallback(() => {
    return Boolean(interactionLockedRef && interactionLockedRef.current);
  }, [interactionLockedRef]);

  const scheduleOverlayHide = useCallback(() => {
    clearHideTimer();
    if (controlsLockedNow()) return;
    const p = player;
    if (!p || p.isDisposed() || p.paused()) return;
    var delay = CONTROLS_HIDE_DELAY_MS;
    var remain = holdUntilRef.current - Date.now();
    if (remain > delay) delay = remain;
    hideTimerRef.current = setTimeout(
      () => hidePeekOverlay(),
      delay
    );
  }, [clearHideTimer, controlsLockedNow, hidePeekOverlay, player]);

  /** آخرین وضعیت پخش برای رِندر ری‌اکت */
  useEffect(() => {
    const p = player;
    if (!p || p.isDisposed()) return;

    const refreshUi = () => {
      setPaused(Boolean(p.paused()));
      setMuted(Boolean(p.muted()));
      const v = p.volume();
      if (typeof v === "number" && Number.isFinite(v)) {
        setVolume(v);
      }
      setCurrent(p.currentTime() ?? 0);
      setDuration(durationOrZero(p));
    };

    refreshUi();

    const ev = (
      ["play", "playing", "pause", "volumechange", "timeupdate",
        "loadedmetadata", "durationchange"] as const
    );

    ev.forEach((e) => p.on(e, refreshUi));

    return () => {
      ev.forEach((e) => p.off(e, refreshUi));
    };
  }, [player]);

  /** وضعیت بافرینگ/لودینگ ویدیو برای نمایش اسپینر وسط صفحه */
  useEffect(() => {
    const p = player;
    if (!p || p.isDisposed()) return;

    const showBuffering = () => setBuffering(true);
    const hideBuffering = () => setBuffering(false);

    const onWaiting = ["waiting", "seeking"] as const;
    const onReady = [
      "playing",
      "canplay",
      "canplaythrough",
      "seeked",
      "pause",
      "ended",
      "error",
    ] as const;

    onWaiting.forEach((e) => p.on(e, showBuffering));
    onReady.forEach((e) => p.on(e, hideBuffering));

    return () => {
      onWaiting.forEach((e) => p.off(e, showBuffering));
      onReady.forEach((e) => p.off(e, hideBuffering));
    };
  }, [player]);

  useEffect(() => {
    const syncFs = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", syncFs);
    return () => document.removeEventListener("fullscreenchange", syncFs);
  }, []);

  /** نمایش لایه هنگام pause / مخفی‌شدن تدریجی هنگام play */
  useEffect(() => {
    const p = player;
    if (!p || p.isDisposed()) return;

    const onPause = () => {
      clearHideTimer();
      if (controlsLockedNow()) {
        setPeekOverlaySynced(false);
        return;
      }
      setPeekOverlaySynced(true);
    };

    const onPlay = () => {
      if (controlsLockedNow()) {
        clearHideTimer();
        setPeekOverlaySynced(false);
        return;
      }
      scheduleOverlayHide();
    };

    p.on("pause", onPause);
    p.on("play", onPlay);

    if (p.paused()) {
      setPeekOverlaySynced(true);
    } else {
      scheduleOverlayHide();
    }

    return () => {
      p.off("pause", onPause);
      p.off("play", onPlay);
      clearHideTimer();
    };
  }, [clearHideTimer, controlsLockedNow, player, scheduleOverlayHide, setPeekOverlaySynced]);

  const revealControlsFromInteraction = useCallback(() => {
    if (controlsLockedNow()) return;
    const p = player;
    if (!p || p.isDisposed()) return;
    setPeekOverlaySynced(true);
    clearHideTimer();
    if (!p.paused()) {
      var delay = CONTROLS_HIDE_DELAY_MS;
      var remain = holdUntilRef.current - Date.now();
      if (remain > delay) delay = remain;
      hideTimerRef.current = setTimeout(
        () => hidePeekOverlay(),
        delay
      );
    }
  }, [clearHideTimer, controlsLockedNow, hidePeekOverlay, player, setPeekOverlaySynced]);

  const revealControlsFor = useCallback((ms: number) => {
    if (controlsLockedNow()) return;
    var delay = ms;
    if (delay < 0) delay = 0;
    holdUntilRef.current = Date.now() + delay;
    setPeekOverlaySynced(true);
    clearHideTimer();
    hideTimerRef.current = setTimeout(function () {
      hidePeekOverlay();
    }, delay);
  }, [clearHideTimer, controlsLockedNow, hidePeekOverlay, setPeekOverlaySynced]);

  const hideControlsNow = useCallback(() => {
    holdUntilRef.current = 0;
    clearHideTimer();
    setPeekOverlaySynced(false);
  }, [clearHideTimer, setPeekOverlaySynced]);

  /** حرکت موس نوار را نشان می‌دهد. پخش/توقف فقط با کلیک یا تاچ کامل روی سطح فیلم است. */
  useEffect(() => {
    const shell = wrapperRef.current;
    if (!shell || !player) return;

    var gesture: { x: number; y: number; inZone: boolean } | null = null;
    var ignoreMouseUntil = 0;

    var pointOfMouse = function (e: MouseEvent) {
      return { x: e.clientX, y: e.clientY };
    };

    var pointOfTouch = function (e: TouchEvent) {
      var t = e.changedTouches && e.changedTouches.length ? e.changedTouches[0] : null;
      if (!t && e.touches && e.touches.length) t = e.touches[0];
      if (!t) return null;
      return { x: t.clientX, y: t.clientY };
    };

    var togglePlayback = function () {
      var p = player;
      if (!p || p.isDisposed()) return;
      if (p.paused()) {
        void p.play();
      } else {
        void p.pause();
      }
    };

    var beginGesture = function (target: EventTarget | null, x: number, y: number) {
      if (controlsLockedNow()) return;
      var inZone = isInControlsZone(target);
      gesture = { x: x, y: y, inZone: inZone };
      if (inZone) {
        revealControlsFromInteraction();
      }
    };

    var endGesture = function (target: EventTarget | null, x: number, y: number) {
      var g = gesture;
      gesture = null;
      if (!g) return;
      if (controlsLockedNow()) return;
      var dx = x - g.x;
      var dy = y - g.y;
      if (dx < 0) dx = -dx;
      if (dy < 0) dy = -dy;
      if (dx > TAP_MOVE_PX || dy > TAP_MOVE_PX) return;
      if (g.inZone) return;
      if (!isPlaySurface(target)) return;
      togglePlayback();
    };

    const onMouseMove = function () {
      if (controlsLockedNow()) return;
      if (window.matchMedia && window.matchMedia("(hover: hover)").matches) {
        revealControlsFromInteraction();
      }
    };

    const onMouseDown = function (e: MouseEvent) {
      if (Date.now() < ignoreMouseUntil) return;
      var pt = pointOfMouse(e);
      beginGesture(e.target, pt.x, pt.y);
    };

    const onMouseUp = function (e: MouseEvent) {
      if (Date.now() < ignoreMouseUntil) return;
      var pt = pointOfMouse(e);
      endGesture(e.target, pt.x, pt.y);
    };

    const onTouchStart = function (e: TouchEvent) {
      ignoreMouseUntil = Date.now() + 700;
      if (e.touches && e.touches.length > 1) {
        gesture = null;
        return;
      }
      var pt = pointOfTouch(e);
      if (!pt) return;
      beginGesture(e.target, pt.x, pt.y);
    };

    const onTouchEnd = function (e: TouchEvent) {
      ignoreMouseUntil = Date.now() + 700;
      var pt = pointOfTouch(e);
      if (!pt) {
        gesture = null;
        return;
      }
      endGesture(e.target, pt.x, pt.y);
    };

    const onTouchCancel = function () {
      gesture = null;
    };

    shell.addEventListener("mousemove", onMouseMove, false);
    shell.addEventListener("mousedown", onMouseDown, false);
    shell.addEventListener("touchstart", onTouchStart, false);
    window.addEventListener("mouseup", onMouseUp, false);
    window.addEventListener("touchend", onTouchEnd, false);
    window.addEventListener("touchcancel", onTouchCancel, false);
    return function () {
      shell.removeEventListener("mousemove", onMouseMove, false);
      shell.removeEventListener("mousedown", onMouseDown, false);
      shell.removeEventListener("touchstart", onTouchStart, false);
      window.removeEventListener("mouseup", onMouseUp, false);
      window.removeEventListener("touchend", onTouchEnd, false);
      window.removeEventListener("touchcancel", onTouchCancel, false);
    };
  }, [
    clearHideTimer,
    controlsLockedNow,
    player,
    revealControlsFromInteraction,
    setPeekOverlaySynced,
    wrapperRef,
  ]);

  const safeDuration = duration > 0 ? duration : 0;

  /** بعد از هر میانبر کیبورد نوار کنترل را نشان می‌دهد (مثل حرکت موس) */
  const revealControlsBriefly = useCallback(() => {
    revealControlsFromInteraction();
  }, [revealControlsFromInteraction]);

  return {
    current,
    duration,
    fullscreen,
    muted,
    paused,
    peekOverlay,
    buffering,
    safeDuration,
    scrubWasPlayingRef,
    volume,
    /** درصد پیشروی نوار زمان برای استایل */
    timeRailPercent:
      safeDuration > 0
        ? Math.min(100, (current / safeDuration) * 100)
        : 0,
    volRailPercent: (muted ? 0 : volume) * 100,
    clearHideTimer,
    scheduleOverlayHide,
    revealControlsBriefly,
    revealControlsFor,
    hideControlsNow,
  };
}

function nearestPlaybackRateStepIndex(rate: number): number {
  let idx = 0;
  let best = Infinity;
  RATE_KEYBOARD_STEPS.forEach((r, i) => {
    const d = Math.abs(r - rate);
    if (d < best || (d === best && r <= RATE_KEYBOARD_STEPS[idx])) {
      best = d;
      idx = i;
    }
  });
  return idx;
}

function stepPlaybackPresets(p: VideoJsPlayer, direction: -1 | 1) {
  try {
    const cur =
      typeof p.playbackRate === "function" ? Number(p.playbackRate()) : 1;
    const next = Math.min(
      RATE_KEYBOARD_STEPS.length - 1,
      Math.max(0, nearestPlaybackRateStepIndex(cur) + direction)
    );
    p.playbackRate(RATE_KEYBOARD_STEPS[next]);
  } catch {
    //
  }
}

function cyclePlaybackQuality(
  urls: readonly string[],
  current: string,
  delta: number,
  cb: (u: string) => void
) {
  if (urls.length < 2) return;
  const i = urls.indexOf(current);
  const next = urls[(Math.max(i, 0) + delta + urls.length) % urls.length];
  if (next) cb(next);
}

function cycleCaptions(opts: readonly { value: string }[], cur: string, cb: (v: string) => void) {
  if (opts.length <= 1) return;
  const i = opts.findIndex((o) => o.value === cur);
  const nx = opts[(Math.max(i, 0) + 1) % opts.length];
  cb(nx.value);
}

async function togglePictureInPicture(p: VideoJsPlayer) {
  if (!document.pictureInPictureEnabled) return;
  try {
    const root = typeof p.el === "function" ? p.el() : null;
    const vid = root?.querySelector("video");
    if (!(vid instanceof HTMLVideoElement)) return;
    if (document.pictureInPictureElement === vid) {
      await document.exitPictureInPicture();
      return;
    }
    await vid.requestPictureInPicture();
  } catch {
    //
  }
}

function usePlayerKeyboardShortcuts(args: {
  captionItems: readonly { label: string; value: string }[];
  captionValue: string;
  onBack: () => void;
  onCaptionChange: (next: string) => void;
  onStreamChange: (url: string | null) => void;
  onSeekFeedback: (seconds: number) => void;
  onVolumeFeedback: (volume: number, muted: boolean) => void;
  onTextFeedback: (text: string) => void;
  onCycleAspect: () => void;
  onToggleLock: () => void;
  playbackOptions: PlayerDownloadOption[];
  player: VideoJsPlayer | null;
  revealControlsBriefly: () => void;
  streamUrl: string;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  tvLayerRef: React.MutableRefObject<TvLayerBridge>;
}) {
  const refArgs = useRef(args);
  refArgs.current = args;

  useEffect(() => {
    var volumeDir = 0;
    var volumeTimer: number | null = null;

    var stopVolumeHold = function () {
      volumeDir = 0;
      if (volumeTimer != null) {
        window.clearInterval(volumeTimer);
        volumeTimer = null;
      }
    };

    var stepVolume = function (dir: number) {
      var a = refArgs.current;
      var p = a.player;
      if (!p || p.isDisposed()) return;
      var currentVol = p.volume();
      var v = typeof currentVol === "number" ? currentVol : 1;
      v = v + dir * VOLUME_KEYBOARD_STEP;
      if (v < 0) v = 0;
      if (v > 1) v = 1;
      p.volume(v);
      if (v > 0 && p.muted()) p.muted(false);
      a.onVolumeFeedback(v, v <= 0 ? true : Boolean(p.muted()));
    };

    var startVolumeHold = function (dir: number) {
      if (volumeTimer != null && volumeDir === dir) return;
      stopVolumeHold();
      volumeDir = dir;
      stepVolume(dir);
      volumeTimer = window.setInterval(function () {
        stepVolume(dir);
      }, VOLUME_HOLD_INTERVAL_MS);
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      const a = refArgs.current;
      const p = a.player;
      if (!p || p.isDisposed()) return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;

      var activeNode = document.activeElement;
      var activeHtml =
        activeNode && activeNode.nodeType === 1 ? (activeNode as HTMLElement) : null;

      if (isBackKey(ev, activeHtml)) {
        ev.preventDefault();
        if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
        if (a.tvLayerRef.current.closeTopLayer()) return;
        if (document.fullscreenElement) {
          a.revealControlsBriefly();
          void document.exitFullscreen();
          return;
        }
        a.onBack();
        return;
      }

      var media = mediaAction(ev);
      if (media === "play") {
        if (ev.repeat) return;
        ev.preventDefault();
        if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
        a.revealControlsBriefly();
        void (p.paused() ? p.play() : p.pause());
        return;
      }

      const wrap = a.wrapperRef.current;
      const dur = durationOrZero(p);
      const t = p.currentTime() || 0;
      const nav = navigationKey(ev);
      const code = physicalKey(ev);
      const bridge = a.tvLayerRef.current;

      if (media === "rewind" || media === "forward") {
        ev.preventDefault();
        if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
        var mediaStep = media === "rewind" ? -SEEK_STEP_MAIN_S : SEEK_STEP_MAIN_S;
        var mediaNext = t + mediaStep;
        if (mediaNext < 0) mediaNext = 0;
        if (dur > 0 && mediaNext > dur) mediaNext = dur;
        p.currentTime(mediaNext);
        a.onSeekFeedback(mediaNext);
        return;
      }

      if (nav !== "ArrowUp" && nav !== "ArrowDown") {
        stopVolumeHold();
      }

      const reveal = () => {
        ev.preventDefault();
        a.revealControlsBriefly();
      };

      const seekClamp = (next: number) => {
        var target = next;
        if (target < 0) target = 0;
        if (dur > 0 && target > dur) target = dur;
        p.currentTime(target);
      };

      const seekQuiet = (delta: number) => {
        ev.preventDefault();
        seekClamp(t + delta);
        var shown = p.currentTime();
        var sec = typeof shown === "number" && isFinite(shown) ? shown : t + delta;
        a.onSeekFeedback(sec);
      };

      if (nav === "ArrowLeft" || nav === "ArrowRight") {
        if (bridge.layerOpen) return;
        if (!bridge.overlayVisible) {
          var hiddenStep = ev.shiftKey ? SEEK_STEP_SHIFT_ARROW_S : SEEK_STEP_ARROW_S;
          seekQuiet(nav === "ArrowLeft" ? -hiddenStep : hiddenStep);
          return;
        }
        if (isTimeSliderFocused(activeHtml)) {
          var sliderStep = ev.shiftKey ? SEEK_STEP_SHIFT_ARROW_S : SEEK_STEP_ARROW_S;
          seekQuiet(nav === "ArrowLeft" ? -sliderStep : sliderStep);
          return;
        }
        if (!isInsidePlayerControls(activeHtml)) {
          ev.preventDefault();
          a.revealControlsBriefly();
          focusPlayerPlayButton();
          return;
        }
        return;
      }

      if (nav === "ArrowUp" || nav === "ArrowDown") {
        if (bridge.layerOpen) return;
        if (!bridge.overlayVisible) {
          ev.preventDefault();
          if (ev.repeat) return;
          startVolumeHold(nav === "ArrowUp" ? 1 : -1);
          return;
        }
        if (!isInsidePlayerControls(activeHtml)) {
          ev.preventDefault();
          a.revealControlsBriefly();
          focusPlayerPlayButton();
          return;
        }
        return;
      }

      if ((nav === " " || nav === "Enter" || code === "KeyK") && !bridge.layerOpen) {
        var nativeTarget =
          activeHtml &&
          (activeHtml.tagName === "BUTTON" || activeHtml.tagName === "A") &&
          isShown(activeHtml);
        if (!nativeTarget && (!bridge.overlayVisible || !isInsidePlayerControls(activeHtml))) {
          if (ev.repeat) return;
          reveal();
          void (p.paused() ? p.play() : p.pause());
          return;
        }
      }

      if (suppressPlayerKeyboard(ev)) return;

      if (nav === " " || nav === "Enter" || code === "KeyK") {
        if (bridge.layerOpen) return;
        if (ev.repeat) return;
        reveal();
        void (p.paused() ? p.play() : p.pause());
        return;
      }

      if (nav === "Home") {
        ev.preventDefault();
        seekClamp(0);
        a.onSeekFeedback(0);
        return;
      }

      if (nav === "End") {
        ev.preventDefault();
        var endAt = dur > 0 ? Math.max(0, dur - 1) : 0;
        seekClamp(endAt);
        a.onSeekFeedback(endAt);
        return;
      }

      var digit = digitFromPhysical(code);
      if (digit != null && !ev.repeat && !ev.shiftKey) {
        ev.preventDefault();
        var jump = digit === 0 || dur <= 0 ? 0 : (dur * digit) / 10;
        seekClamp(jump);
        a.onSeekFeedback(jump);
        return;
      }

      if (code === "KeyM") {
        if (ev.repeat) return;
        reveal();
        p.muted(!p.muted());
        return;
      }

      if (code === "KeyF") {
        if (ev.repeat || !wrap) return;
        reveal();
        void (document.fullscreenElement
          ? document.exitFullscreen()
          : wrap.requestFullscreen());
        return;
      }

      if (nav === "Escape") {
        if (!document.fullscreenElement || ev.repeat) return;
        reveal();
        void document.exitFullscreen();
        return;
      }

      if (code === "KeyB") {
        if (document.fullscreenElement || ev.repeat) return;
        reveal();
        a.onBack();
        return;
      }

      if (code === "KeyC") {
        if (ev.repeat) return;
        ev.preventDefault();
        var opts = a.captionItems;
        if (opts.length <= 1) return;
        var found = -1;
        var ci = 0;
        for (ci = 0; ci < opts.length; ci += 1) {
          if (opts[ci].value === a.captionValue) found = ci;
        }
        var nextCap = opts[(found < 0 ? 0 : found + 1) % opts.length];
        a.onCaptionChange(nextCap.value);
        a.onTextFeedback(nextCap.label);
        return;
      }

      if (code === "BracketLeft" || code === "BracketRight") {
        if (ev.repeat) return;
        ev.preventDefault();
        var urls: string[] = [];
        var oi = 0;
        for (oi = 0; oi < a.playbackOptions.length; oi += 1) {
          urls.push(a.playbackOptions[oi].url);
        }
        if (urls.length < 2) return;
        var qIndex = urls.indexOf(a.streamUrl);
        if (qIndex < 0) qIndex = 0;
        var delta = code === "BracketLeft" ? -1 : 1;
        var nextUrl = urls[(qIndex + delta + urls.length) % urls.length];
        if (!nextUrl) return;
        a.onStreamChange(nextUrl);
        var qLabel = nextUrl;
        for (oi = 0; oi < a.playbackOptions.length; oi += 1) {
          if (a.playbackOptions[oi].url === nextUrl) {
            qLabel = a.playbackOptions[oi].quality;
            break;
          }
        }
        a.onTextFeedback(qLabel);
        return;
      }

      if (code === "Comma" || code === "Period") {
        if (ev.shiftKey) {
          if (ev.repeat) return;
          reveal();
          stepPlaybackPresets(p, code === "Comma" ? -1 : 1);
          return;
        }
        seekQuiet(code === "Comma" ? -SEEK_STEP_ARROW_S : SEEK_STEP_ARROW_S);
        return;
      }

      if (code === "KeyP" || code === "KeyI") {
        if (ev.repeat) return;
        reveal();
        void togglePictureInPicture(p);
        return;
      }

      if (code === "KeyJ" || code === "KeyL") {
        seekQuiet(code === "KeyJ" ? -SEEK_STEP_MAIN_S : SEEK_STEP_MAIN_S);
        return;
      }

      if (code === "KeyA") {
        if (ev.repeat) return;
        ev.preventDefault();
        a.onCycleAspect();
        return;
      }

      if (code === "KeyQ") {
        if (ev.repeat) return;
        ev.preventDefault();
        a.onToggleLock();
        return;
      }
    };

    const onKeyUp = (ev: KeyboardEvent) => {
      var nav = navigationKey(ev);
      if (nav === "ArrowUp" || nav === "ArrowDown") {
        stopVolumeHold();
      }
    };

    const onBlur = () => {
      stopVolumeHold();
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("blur", onBlur);
    return () => {
      stopVolumeHold();
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("blur", onBlur);
    };
  }, []);
}

// ---------------------------------------------------------------------------
// صفحهٔ پلیر
// ---------------------------------------------------------------------------

export default function PlayerPage() {
  const routeParams = useParams<{ id: string }>();
  const history = useHistory();
  const queryParams = useQueryParams();
  const id = parseInt(routeParams.id || "0", 10) || 0;
  const season = parseInt(getQueryParam(queryParams, "season", "0"), 10) || 0;
  const episode = parseInt(getQueryParam(queryParams, "episode", "0"), 10) || 0;
  const quality = getQueryParam(queryParams, "quality", "");
  const rawStreamType = getQueryParam(queryParams, "type", "");
  const currentStreamType: SearchParamsShape["type"] =
    rawStreamType === "farsi_dubbed" ||
    rawStreamType === "with_subtitle" ||
    rawStreamType === "without_subtitle"
      ? rawStreamType
      : "with_subtitle";

  const [playerData, setPlayerData] = useState<PlayerApiData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [goToSubscription, setGoToSubscription] = useState(false);
  const [currentQuality] = useState<string>(quality);
  const [currentQualityCode, setCurrentQualityCode] = useState<string | null>(null);
  const [pickedStreamUrl, setPickedStreamUrl] = useState<string | null>(null);
  const [userSubtitles, setUserSubtitles] = useState<PlayerSubtitle[]>([]);
  const userSubtitlesRef = useRef(userSubtitles);
  userSubtitlesRef.current = userSubtitles;

  const subtitlesForTracks = useMemo((): PlayerSubtitle[] => {
    if (!playerData) return [...userSubtitles];
    return [...playerData.subtitles, ...userSubtitles];
  }, [playerData, userSubtitles]);

  const playbackOptions = useMemo((): PlayerDownloadOption[] => {
    if (!playerData) return [];
    const typed = playerData.download_options.filter(
      (o) => o.type === currentStreamType
    );
    return typed.length ? typed : [...playerData.download_options];
  }, [playerData, currentStreamType]);

  const defaultStreamUrl = useMemo(() => {
    if (!playbackOptions.length) return "";
    if (currentQuality) {
      const match = playbackOptions.find(
        (o) =>
          o.quality === currentQuality || o.quality_code === currentQuality
      );
      if (match) {
        return match.url;
      }
    }
    return playbackOptions[0].url;
  }, [currentQuality, playbackOptions]);

  const effectiveStreamUrl = pickedStreamUrl ?? defaultStreamUrl;

  useEffect(() => {
    const match = playbackOptions.find((o) => o.url === effectiveStreamUrl);
    setCurrentQualityCode(match ? match.quality_code : null);
  }, [effectiveStreamUrl, playbackOptions]);

  const selectedPlayback = useMemo(
    () => playbackOptions.find((o) => o.url === effectiveStreamUrl) ?? null,
    [effectiveStreamUrl, playbackOptions]
  );

  const mediaSrc = effectiveStreamUrl;

  const mimeType = useMemo(
    () => mimeFromFormat(selectedPlayback?.format),
    [selectedPlayback?.format]
  );

  const canToggleDubSubtitle = useMemo(() => {
    if (!playerData) return false; 
    return (
      playerData.download_options.some((o) => o.type === "farsi_dubbed") &&
      playerData.download_options.some((o) => o.type !== "farsi_dubbed")
    );
  }, [playerData]);

  const toggleDubSubtitle = useCallback(() => {
    if (!playerData) return;
    const hasDub = playerData.download_options.some(
      (o) => o.type === "farsi_dubbed"
    );
    const hasSub = playerData.download_options.some(
      (o) => o.type === "with_subtitle"
    );
    if (!hasDub || !hasSub) return;
    const next: SearchParamsShape["type"] =
      currentStreamType === "farsi_dubbed" ? "with_subtitle" : "farsi_dubbed";
    const query = new URLSearchParams({
      season: String(season),
      episode: String(episode),
      quality: currentQuality,
      type: next,
    });
    history.replace(`/player/${id}?${query.toString()}`);
  }, [
    currentQuality,
    currentStreamType,
    episode,
    id,
    playerData,
    history,
    season,
  ]);

  useEffect(() => {
    setPickedStreamUrl(null);
  }, [currentQuality, currentStreamType, episode, id, season]);

  useEffect(() => {
    return () => {
      for (const s of userSubtitlesRef.current) {
        if (s.url.startsWith("blob:")) URL.revokeObjectURL(s.url);
      }
    };
  }, []);

  useEffect(() => {
    setUserSubtitles((prev) => {
      for (const s of prev) {
        if (s.url.startsWith("blob:")) URL.revokeObjectURL(s.url);
      }
      return [];
    });
  }, [episode, id, season]);

  useEffect(function () {
    var cancelled = false;
    setIsLoading(true);
    setPlayerData(null);

    videoRepo.getPlayerData(id, season, episode).then(function (res) {
      if (cancelled) {
        return;
      }

      var payload = res.data;
      if (!payload) {
        if (res.message === "subscribe_required") {
          setGoToSubscription(true);
        }
        setIsLoading(false);
        return;
      }

      try {
        var rawSubtitles = Array.isArray(payload.subtitles) ? payload.subtitles : [];
        var subtitles = rawSubtitles.map(function (s) {
          var subUrl = s && s.url ? String(s.url) : "";
          return Object.assign({}, s, {
            url:
              subUrl.indexOf("http") === 0
                ? subUrl
                : ASSETS_BASE_URL + subUrl.replace("files/", ""),
          });
        });
        var rawOptions = Array.isArray(payload.download_options)
          ? payload.download_options
          : [];
        var download_options = rawOptions.map(function (opt) {
          var spriteFields = readSpriteFields(opt);
          return Object.assign({}, opt, spriteFields, {
            url: resolvePlayerMediaUrl(opt && opt.url ? String(opt.url) : ""),
          });
        });
        var parsedEpisodes: EpisodeListItem[] = [];
        parseEpisodesAny(payload.episodes, season || 1, parsedEpisodes);
        var episodes = Array.from(
          new Map(
            parsedEpisodes
              .sort(function (a, b) {
                return a.season - b.season || a.episode - b.episode;
              })
              .map(function (ep) {
                return [ep.season + "-" + ep.episode, Object.assign({}, ep)];
              })
          ).values()
        ).map(function (ep) {
          return {
            name: ep.name || "قسمت " + ep.episode,
            episode: ep.episode,
            season: ep.season,
          };
        });

        setPlayerData(Object.assign({}, payload, {
          subtitles: subtitles,
          episodes: episodes,
          download_options: download_options,
        }));
      } catch (err) {
        var fallbackOptions = Array.isArray(payload.download_options)
          ? payload.download_options.map(function (opt) {
              var spriteFields = readSpriteFields(opt);
              return Object.assign({}, opt, spriteFields, {
                url: resolvePlayerMediaUrl(opt && opt.url ? String(opt.url) : ""),
              });
            })
          : [];
        setPlayerData(Object.assign({}, payload, {
          download_options: fallbackOptions,
        }));
      }

      setIsLoading(false);
    }).catch(function () {
      if (!cancelled) {
        setIsLoading(false);
      }
    });

    return function () {
      cancelled = true;
    };
  }, [episode, id, season]);

  if (isLoading) {
    return (
      <div className="player-page__loading">
        <div className="player-page__loading-inner">
          <img src={loadingGif} alt="loading" />
        </div>
      </div>
    );
  }

  if (goToSubscription) {
    return <Redirect to="/profile/subscription" />;
  }

  if (!playerData) {
    return <Redirect to="/404" />;
  }

  if (!effectiveStreamUrl) {
    return (
      <div className="player-page__error">
        <div className="player-page__error-inner">
          منبع پخش برای این عنوان در دسترس نیست.
        </div>
      </div>
    );
  }

  return (
    <div
      key={id + "-" + season + "-" + episode}
      className="player-page"
      dir="ltr"
    >
      <VideoJsBridge
        wrapClassName="player-page__video-host"
        mediaSrc={mediaSrc}
        mimeType={mimeType}
        subtitles={subtitlesForTracks}
      >
        <CustomControls
          canToggleDubSubtitle={canToggleDubSubtitle}
          movieId={id}
          currentQualityCode={currentQualityCode ?? ""}
          currentEpisode={episode}
          currentSeason={season}
          onBack={() => history.goBack()}
          onEpisodeSelect={(pickedSeason, pickedEpisode) => {
            if (!playerData?.is_series) return;
            const query = new URLSearchParams({
              season: String(pickedSeason),
              episode: String(pickedEpisode),
              quality: currentQuality,
              type: currentStreamType,
            });
            history.push(`/player/${id}?${query.toString()}`);
          }}
          onStreamChange={setPickedStreamUrl}
          onToggleDubSubtitle={toggleDubSubtitle}
          playbackOptions={playbackOptions}
          playbackStreamType={currentStreamType}
          playerData={playerData}
          streamUrl={effectiveStreamUrl}
          subtitlesForTracks={subtitlesForTracks}
          onUserSubtitleImported={(sub) => setUserSubtitles((p) => [...p, sub])}
        />
      </VideoJsBridge>
    </div>
  );
}

// ---------------------------------------------------------------------------
// نوار کنترل (UI بدون Video.js پیش‌فرض)
// ---------------------------------------------------------------------------

function buildCaptionItems(subtitles: PlayerSubtitle[]) {
  return [
    { value: "off" as const, label: "زیرنویس خاموش" },
    ...subtitles.map((s, idx) => ({
      value: `sub-${idx}` as const,
      label: s.dubbed ? `${s.name} (دوبله)` : s.name,
    })),
  ];
}

/** وقتی ورود با `type=with_subtitle` است، اولین ترک زیرنویس به‌صورت پیش‌فرض روشن شود. */
function defaultCaptionForInitialStream(
  streamType: SearchParamsShape["type"],
  subtitleCount: number
): string {
  if (streamType === "with_subtitle" && subtitleCount > 0) return "sub-0";
  return "off";
}

const LOCAL_SUBTITLE_MAX_BYTES = 2 * 1024 * 1024;

function stripBom(s: string): string {
  if (s.length > 0 && s.charCodeAt(0) === 0xfeff) return s.slice(1);
  return s;
}

function srtTimeToVtt(timePart: string): string {
  return timePart.trim().replace(/,/g, ".");
}

/** تبدیل سادهٔ SRT به WebVTT برای ترک native / Video.js. */
function srtToWebVtt(srtRaw: string): string {
  const normalized = stripBom(srtRaw.replace(/\r\n/g, "\n").replace(/\r/g, "\n")).trim();
  if (!normalized) return "WEBVTT\n\n";
  const blocks = normalized.split(/\n\n+/);
  const cues: string[] = [];
  for (const block of blocks) {
    const rows = block.split("\n").filter((r) => r.length > 0);
    if (rows.length < 2) continue;
    let rowIdx = 0;
    if (/^\d+$/.test(rows[rowIdx].trim())) rowIdx += 1;
    const timeLine = rows[rowIdx];
    if (!timeLine || !/-->/.test(timeLine)) continue;
    const parts = timeLine.split("-->");
    if (parts.length < 2) continue;
    const start = srtTimeToVtt(parts[0] ?? "");
    const endRaw = parts.slice(1).join("-->");
    const end = srtTimeToVtt((endRaw.split(/\s+/)[0] ?? "").trim());
    const textRows = rows.slice(rowIdx + 1);
    if (!textRows.length) continue;
    cues.push(`${start} --> ${end}\n${textRows.join("\n")}`);
  }
  return `WEBVTT\n\n${cues.join("\n\n")}\n`;
}

function userSubtitleFileLabel(fileName: string): string {
  return fileName.replace(/\.(srt|vtt)$/i, "").trim() || "زیرنویس محلی";
}

function ensureWebVttHeader(body: string): string {
  const t = stripBom(body.replace(/\r\n/g, "\n").replace(/\r/g, "\n")).trimStart();
  if (/^WEBVTT(?:\s|$)/i.test(t)) return t.endsWith("\n") ? t : `${t}\n`;
  return `WEBVTT\n\n${t}\n`;
}

type EpisodeListItem = {
  season: number;
  episode: number;
  name: string;
};

function resolveEpisodeSeason(ep: { name: string; episode: number; season?: number }, fallbackSeason: number) {
  if (typeof ep.season === "number" && Number.isFinite(ep.season) && ep.season > 0) {
    return ep.season;
  }
  const name = ep.name ?? "";
  const seasonMatch =
    name.match(/(?:فصل|season)\s*([0-9۰-۹]+)/i) ??
    name.match(/S\s*([0-9]+)/i);
  if (!seasonMatch?.[1]) return fallbackSeason;
  const faDigits = "۰۱۲۳۴۵۶۷۸۹";
  const normalized = seasonMatch[1]
    .split("")
    .map((c) => {
      const idx = faDigits.indexOf(c);
      return idx >= 0 ? String(idx) : c;
    })
    .join("");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackSeason;
}

function asPositiveNumber(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function pushEpisodeItem(
  out: EpisodeListItem[],
  item: { season: number; episode: number; name: string }
) {
  if (!Number.isFinite(item.season) || item.season <= 0) return;
  if (!Number.isFinite(item.episode) || item.episode <= 0) return;
  out.push(item);
}

function parseEpisodesAny(
  input: unknown,
  fallbackSeason: number,
  out: EpisodeListItem[]
) {
  if (!input) return;

  if (Array.isArray(input)) {
    for (const raw of input) {
      if (raw && typeof raw === "object") {
        const rec = raw as Record<string, unknown>;
        pushEpisodeItem(out, {
          season: asPositiveNumber(rec.season) ?? fallbackSeason,
          episode: asPositiveNumber(rec.episode) ?? 0,
          name: String(rec.name ?? rec.title ?? ""),
        });
        // بعضی APIها items را هم داخل هر اپیزود می‌فرستند
        if (rec.items) parseEpisodesAny(rec.items, asPositiveNumber(rec.season) ?? fallbackSeason, out);
      }
    }
    return;
  }

  if (typeof input !== "object") return;

  var obj = input as Record<string, unknown>;
  var key: string;
  for (key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
      continue;
    }
    var v = obj[key];
    // کلیدهایی مثل "1", "season_2", "فصل 3"
    var seasonFromKey = (function () {
      var m = key.match(/(\d+)/);
      return m ? asPositiveNumber(m[1]) : null;
    })();
    var seasonBase = seasonFromKey !== null && seasonFromKey !== undefined ? seasonFromKey : fallbackSeason;

    if (Array.isArray(v)) {
      parseEpisodesAny(v, seasonBase, out);
      continue;
    }
    if (v && typeof v === "object") {
      var rec = v as Record<string, unknown>;
      // حالت آبجکت اپیزود تکی
      if ("episode" in rec || "name" in rec || "title" in rec) {
        var recSeason = asPositiveNumber(rec.season);
        var recEpisode = asPositiveNumber(rec.episode);
        pushEpisodeItem(out, {
          season: recSeason !== null && recSeason !== undefined ? recSeason : seasonBase,
          episode: recEpisode !== null && recEpisode !== undefined ? recEpisode : 0,
          name: String(rec.name !== undefined && rec.name !== null ? rec.name : rec.title !== undefined && rec.title !== null ? rec.title : ""),
        });
      }
      // حالت wrapper با items
      if (rec.items) {
        var itemsSeason = asPositiveNumber(rec.season);
        parseEpisodesAny(rec.items, itemsSeason !== null && itemsSeason !== undefined ? itemsSeason : seasonBase, out);
      } else {
        parseEpisodesAny(rec, seasonBase, out);
      }
    }
  }
}

function CustomControls({
  canToggleDubSubtitle,
  movieId,
  currentQualityCode,
  currentEpisode,
  currentSeason,
  onEpisodeSelect,
  onBack,
  onStreamChange,
  onToggleDubSubtitle,
  playbackOptions,
  playbackStreamType,
  playerData,
  streamUrl,
  subtitlesForTracks,
  onUserSubtitleImported,
}: CustomControlsProps) {
  const { player, wrapperRef } = useVideoJs();

  const {setNotification} = useContext<AppContextType>(WonmovContext);

  const [captionValue, setCaptionValue] = useState(() =>
    defaultCaptionForInitialStream(
      playbackStreamType,
      subtitlesForTracks.length
    )
  );
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [showAudioMenu, setShowAudioMenu] = useState(false);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [showEpisodesDrawer, setShowEpisodesDrawer] = useState(false);
  const [showScoreAlert, setShowScoreAlert] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(
    () => (playerData.watched_time ?? 0) > 0
  );
  const [earnedScore, setEarnedScore] = useState(10);
  const [isSavedWatchTime, setIsSavedWatchTime] = useState(false);
  const scoreAwardedRef = useRef(false);
  const blockScoreRef = useRef(false);
  const resumeKeepPausedHandlerRef = useRef<(() => void) | null>(null);
  const [controlsLocked, setControlsLocked] = useState(false);
  const controlsLockedRef = useRef(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showNextEpisode, setShowNextEpisode] = useState(false);
  const [nextFillOn, setNextFillOn] = useState(false);
  const [playerHud, setPlayerHud] = useState<PlayerHudState | null>(null);
  const [pipActive, setPipActive] = useState(false);
  const [timeHover, setTimeHover] = useState<{
    left: number;
    text: string;
    frame: SpriteFrame | null;
  } | null>(null);
  const [spriteCues, setSpriteCues] = useState<SpriteCue[]>([]);
  const spriteMapRef = useRef<SpriteSheetMap>({});
  const spriteSheetRef = useRef<{
    [url: string]: { w: number; h: number; src: string } | "fail" | "loading";
  }>({});
  const hoverPointRef = useRef<{ el: HTMLElement; clientX: number } | null>(null);
  const refreshHoverRef = useRef<(() => void) | null>(null);
  var activeSprite = readSpriteFields(
    playbackOptions.filter(function (opt) {
      return opt.url === streamUrl;
    })[0] || null
  );
  spriteMapRef.current = activeSprite.sprites;

  useEffect(function () {
    var previewUrl = activeSprite.preview;
    setSpriteCues([]);
    if (!previewUrl) return;
    var cancelled = false;
    fetchSpriteVtt(previewUrl).then(function (text) {
      if (!cancelled) setSpriteCues(parseSpriteVtt(text));
    }).catch(function () {
      if (!cancelled) setSpriteCues([]);
    });
    return function () {
      cancelled = true;
    };
  }, [activeSprite.preview]);

  useEffect(function () {
    if (refreshHoverRef.current) refreshHoverRef.current();
  }, [spriteCues]);
  const bottomControlsRef = useRef<HTMLDivElement | null>(null);
  const controlsOffsetRef = useRef(SUBTITLE_BASE_CONTROLS_OFFSET_PX);
  const showNextRef = useRef(false);
  const nextArmedRef = useRef(true);
  const nextTimerRef = useRef<number | null>(null);
  const hudHideTimerRef = useRef<number | null>(null);
  const hudExitTimerRef = useRef<number | null>(null);
  const cycleAspectRef = useRef(function () {});
  const toggleLockRef = useRef(function () {});
  const seekHudRef = useRef(function (_seconds: number) {});
  const volumeHudRef = useRef(function (_volume: number, _muted: boolean) {});
  const textHudRef = useRef(function (_text: string) {});

  const dismissPlayerMenus = useCallback(() => {
    setShowQualityMenu(false);
    setShowSubtitleMenu(false);
    setShowAudioMenu(false);
    setShowSettingsDrawer(false);
    setShowEpisodesDrawer(false);
  }, []);

  const chrome = useVideoJsChrome(player, wrapperRef, dismissPlayerMenus, controlsLockedRef);

  useEffect(() => {
    const p = player;
    if (!showResumeDialog || !p || p.isDisposed()) return;

    const keepPaused = () => {
      if (!p.paused()) p.pause();
    };

    resumeKeepPausedHandlerRef.current = keepPaused;
    keepPaused();
    p.on("play", keepPaused);

    return () => {
      p.off("play", keepPaused);
      if (resumeKeepPausedHandlerRef.current === keepPaused) {
        resumeKeepPausedHandlerRef.current = null;
      }
    };
  }, [player, showResumeDialog]);

  const releaseResumeDialogPlaybackLock = useCallback(() => {
    const p = player;
    const keepPaused = resumeKeepPausedHandlerRef.current;
    if (p && !p.isDisposed() && keepPaused) {
      p.off("play", keepPaused);
    }
    resumeKeepPausedHandlerRef.current = null;
  }, [player]);

  const seekAndPlay = useCallback((targetSeconds: number) => {
    const p = player;
    if (!p || p.isDisposed()) return;

    const apply = () => {
      const du = p.duration();
      const clamped =
        typeof du === "number" && Number.isFinite(du) && du > 0
          ? Math.min(targetSeconds, Math.max(0, du - 0.2))
          : targetSeconds;
      p.currentTime(Math.max(0, clamped));
      safePlay(p);
    };

    if (p.readyState() >= 1) {
      apply();
    } else {
      p.one("loadedmetadata", apply);
    }
  }, [player]);

  const handleResumeWatching = useCallback(() => {
    releaseResumeDialogPlaybackLock();
    setShowResumeDialog(false);
    playingTime = Math.max(0, Math.floor(playerData.watched_time ?? 0));
    seekAndPlay(playingTime);
  }, [playerData.watched_time, releaseResumeDialogPlaybackLock, seekAndPlay]);

  const handleStartWatchingFromBeginning = useCallback(() => {
    releaseResumeDialogPlaybackLock();
    setShowResumeDialog(false);
    playingTime = 0;
    seekAndPlay(0);
  }, [releaseResumeDialogPlaybackLock, seekAndPlay]);

  useEffect(() => {
    const p = player;
    if (!p || p.isDisposed()) return;

    const savePlaybackProgress = () => {
      if (p.paused() || isPlayerEnded(p)) return;
      playingTime += 30;
      if (
        typeof playingTime !== "number" ||
        !Number.isFinite(playingTime) ||
        playingTime <= 0
      ) {
        return;
      }

      let watchTimes: WatchListItemRequest[] = []
      const watchTimesString = localStorage.getItem("watch-times");
      if (watchTimesString) {
        watchTimes = JSON.parse(watchTimesString);
      }

      watchTimes = watchTimes.filter((item) => item.movie_id !== movieId);
      watchTimes.push({
        movie_id: movieId,
        season: currentSeason,
        episode: currentEpisode,
        time: Math.ceil((player?.currentTime() ?? 0)),
        duration: Math.ceil((player?.duration() ?? 0)),
      });
      localStorage.setItem("watch-times", JSON.stringify(watchTimes));

      const remainingTime = (player.duration() ?? 0) - (player.currentTime() ?? 0);
      if (remainingTime <= 30) {
        clearInterval(intervalId);
        userRepo.saveWatchListItem(watchTimes).then((response) => {
          if (response.status) {
            localStorage.removeItem("watch-times");
          }
        });
        return;
      }

      if (!isSavedWatchTime) {
        let last5Minutes = remainingTime < 300;
        saveWatchTimeForScore(last5Minutes);
        if (last5Minutes) {
          setIsSavedWatchTime(true);
        }
      }
    };

    const intervalId = window.setInterval(
      savePlaybackProgress,
      PLAYBACK_PROGRESS_SAVE_INTERVAL_MS
    );

    return () => window.clearInterval(intervalId);
  }, [player, movieId, currentSeason, currentEpisode]);

  const [expandedSeasons, setExpandedSeasons] = useState<number[]>([currentSeason]);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [subtitleBgColor, setSubtitleBgColor] = useState("sub-dark-bg");
  const [subtitleTextColor, setSubtitleTextColor] = useState("sub-text-white");
  const [subtitleFontFamily, setSubtitleFontFamily] = useState("sub-font-dana");
  const [subtitleTextSize, setSubtitleTextSize] = useState(16);
  const [subtitleMargin, setSubtitleMargin] = useState(0);
  const [subtitleFontWeight, setSubtitleFontWeight] = useState("sub-font-regular");

  const playerUiPersistedSnapshot = useMemo(
    (): PlayerUiSettingsV1 => ({
      v: 1,
      playbackSpeed,
      subtitleBgColor,
      subtitleTextColor,
      subtitleFontFamily,
      subtitleFontWeight,
      subtitleTextSize,
      subtitleMargin,
    }),
    [
      playbackSpeed,
      subtitleBgColor,
      subtitleTextColor,
      subtitleFontFamily,
      subtitleFontWeight,
      subtitleTextSize,
      subtitleMargin,
    ]
  );

  const captionItems = useMemo(
    () => buildCaptionItems(subtitlesForTracks),
    [subtitlesForTracks]
  );

  const tvLayerRef = useRef<TvLayerBridge>({
    overlayVisible: false,
    layerOpen: false,
    closeTopLayer: function () {
      return false;
    },
  });

  usePlayerKeyboardShortcuts({
    captionItems,
    captionValue,
    onBack,
    onCaptionChange: setCaptionValue,
    onStreamChange,
    onSeekFeedback: function (seconds) {
      seekHudRef.current(seconds);
    },
    onVolumeFeedback: function (volume, muted) {
      volumeHudRef.current(volume, muted);
    },
    onTextFeedback: function (text) {
      textHudRef.current(text);
    },
    onCycleAspect: function () {
      cycleAspectRef.current();
    },
    onToggleLock: function () {
      toggleLockRef.current();
    },
    playbackOptions,
    player,
    revealControlsBriefly: chrome.revealControlsBriefly,
    streamUrl,
    wrapperRef,
    tvLayerRef,
  });

  const subtitleTrackKey = useMemo(
    () => subtitlesForTracks.map((s) => `${s.url}:${s.format}`).join("|"),
    [subtitlesForTracks]
  );

  const lastUserBlobUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const last = subtitlesForTracks[subtitlesForTracks.length - 1];
    const lastUrl = last?.url ?? "";
    if (!lastUrl.startsWith("blob:")) return;
    if (lastUrl === lastUserBlobUrlRef.current) return;
    lastUserBlobUrlRef.current = lastUrl;
    const idx = subtitlesForTracks.length - 1;
    const lang = `sub-${idx}`;
    setCaptionValue(lang);
    const p = player;
    if (p && !p.isDisposed()) applyCaptionSelection(p, lang);
  }, [subtitlesForTracks, player]);

  const episodeItems = useMemo<EpisodeListItem[]>(() => {
    // 1) نام قسمت‌ها از endpoint player
    const episodesSrc = Array.isArray(playerData.episodes) ? playerData.episodes : [];
    const fromEpisodes: EpisodeListItem[] = episodesSrc
      .map((ep) => ({
        season: resolveEpisodeSeason(
          ep as { name: string; episode: number; season?: number },
          currentSeason
        ),
        episode: asPositiveNumber((ep as { episode?: unknown }).episode) ?? 0,
        name: String((ep as { name?: unknown }).name ?? ""),
      }))
      .filter((ep) => ep.episode > 0);

    // 2) فصل/قسمت قطعی‌تر از download_options (گاهی episodes فقط نام می‌دهد)
    const fromDownloads: EpisodeListItem[] = (Array.isArray(playerData.download_options)
      ? playerData.download_options
      : []
    )
      .map((opt) => {
        const rec = opt as unknown as Record<string, unknown>;
        const season = asPositiveNumber(rec.season) ?? currentSeason;
        const episode = asPositiveNumber(rec.episode);
        if (!episode) return null;
        return {
          season,
          episode,
          name: "",
        } satisfies EpisodeListItem;
      })
      .filter((item): item is EpisodeListItem => item !== null);

    // ادغام بر اساس فصل/قسمت
    const byKey = new Map<string, EpisodeListItem>();
    for (const item of [...fromDownloads, ...fromEpisodes]) {
      const key = `${item.season}-${item.episode}`;
      const prev = byKey.get(key);
      byKey.set(key, {
        season: item.season,
        episode: item.episode,
        name: item.name || prev?.name || `قسمت ${item.episode}`,
      });
    }

    const merged = Array.from(byKey.values()).sort(
      (a, b) => a.season - b.season || a.episode - b.episode
    );

    // fallback نهایی: اگر همه چیز خالی بود، همان episodes خام را در فصل جاری نشان بده
    if (merged.length > 0) return merged;
    return episodesSrc
      .map((ep) => ({
        season: currentSeason,
        episode: asPositiveNumber((ep as { episode?: unknown }).episode) ?? 0,
        name: String((ep as { name?: unknown }).name ?? ""),
      }))
      .filter((ep) => ep.episode > 0)
      .sort((a, b) => a.episode - b.episode);
  }, [currentSeason, playerData.download_options, playerData.episodes]);

  const seasons = useMemo(
    () => Array.from(new Set(episodeItems.map((ep) => ep.season))).sort((a, b) => a - b),
    [episodeItems]
  );

  const episodesBySeason = useMemo(() => {
    const map = new Map<number, EpisodeListItem[]>();
    for (const ep of episodeItems) {
      if (!map.has(ep.season)) map.set(ep.season, []);
      map.get(ep.season)!.push(ep);
    }
    return map;
  }, [episodeItems]);

  const nextEpisodeItem = useMemo(function () {
    if (!playerData.is_series) return null;
    return findNextEpisodeItem(episodeItems, currentSeason, currentEpisode);
  }, [currentEpisode, currentSeason, episodeItems, playerData.is_series]);
  blockScoreRef.current = Boolean(nextEpisodeItem);

  useEffect(() => {
    if (!player || player.isDisposed()) return;

    // نگهبان ضدِ ورود مجدد: تغییر track.mode خودش رویداد change/texttrackchange
    // صادر می‌کند؛ بدون این محافظ یک حلقه‌ی بی‌نهایت ایجاد می‌شد و مرورگر هنگ می‌کرد.
    let applying = false;
    let scheduled: ReturnType<typeof setTimeout> | null = null;

    const reapply = () => {
      if (applying || player.isDisposed()) return;
      applying = true;
      try {
        applyCaptionSelection(player, captionValue);
      } finally {
        applying = false;
      }
    };

    // فقط رویدادهایی که خودِ ما تولیدشان نمی‌کنیم (افزودن ترک / آماده‌شدن مدیا).
    const scheduleReapply = () => {
      if (scheduled != null) return;
      scheduled = setTimeout(() => {
        scheduled = null;
        reapply();
      }, 0);
    };

    reapply();

    const tt = player.textTracks() as unknown as TextTrackList;
    const rt = player.remoteTextTracks() as unknown as TextTrackList;
    const onTrackListChange = () => scheduleReapply();

    tt.addEventListener("addtrack", onTrackListChange);
    rt.addEventListener("addtrack", onTrackListChange);
    player.on("loadedmetadata", scheduleReapply);
    player.on("canplay", scheduleReapply);

    return () => {
      if (scheduled != null) {
        clearTimeout(scheduled);
        scheduled = null;
      }
      tt.removeEventListener("addtrack", onTrackListChange);
      rt.removeEventListener("addtrack", onTrackListChange);
      player.off("loadedmetadata", scheduleReapply);
      player.off("canplay", scheduleReapply);
    };
  }, [captionValue, player, subtitleTrackKey]);

  useEffect(() => {
    const loaded = readPlayerUiSettingsFromLocalStorage();
    if (!loaded) return;
    setPlaybackSpeed(loaded.playbackSpeed);
    setSubtitleBgColor(loaded.subtitleBgColor);
    setSubtitleTextColor(loaded.subtitleTextColor);
    setSubtitleFontFamily(loaded.subtitleFontFamily);
    setSubtitleFontWeight(loaded.subtitleFontWeight);
    setSubtitleTextSize(loaded.subtitleTextSize);
    setSubtitleMargin(loaded.subtitleMargin);
  }, []);

  useEffect(() => {
    const p = player;
    if (!p || p.isDisposed()) return;
    applyPlayerUiToPlayerInstance(p, playerUiPersistedSnapshot);
  }, [player, playerUiPersistedSnapshot]);

  useEffect(() => {
    scoreAwardedRef.current = false;
    setShowScoreAlert(false);
    setEarnedScore(0);
  }, [streamUrl, playerData.name]);

  useEffect(() => {
    const p = player;
    if (!p || p.isDisposed()) return;

    const onEnded = () => {
      if (scoreAwardedRef.current) return;
      scoreAwardedRef.current = true;
      if (blockScoreRef.current) return;
      setEarnedScore(3);
      setShowScoreAlert(true);
    };

    p.on("ended", onEnded);
    return () => {
      p.off("ended", onEnded);
    };
  }, [player]);

  const overlayVisible =
    !controlsLocked &&
    (chrome.paused ||
      chrome.peekOverlay ||
      showSettingsDrawer ||
      showEpisodesDrawer ||
      showQualityMenu ||
      showSubtitleMenu ||
      showAudioMenu ||
      showResumeDialog);
  const chromeControlPointer = "touch-manipulation";

  tvLayerRef.current = {
    overlayVisible: overlayVisible,
    layerOpen:
      showShortcuts ||
      showScoreAlert ||
      showResumeDialog ||
      showSettingsDrawer ||
      showEpisodesDrawer ||
      showQualityMenu ||
      showSubtitleMenu ||
      showAudioMenu,
    closeTopLayer: function () {
      if (showShortcuts) {
        setShowShortcuts(false);
        return true;
      }
      if (showScoreAlert) {
        setShowScoreAlert(false);
        return true;
      }
      if (showResumeDialog) {
        handleStartWatchingFromBeginning();
        return true;
      }
      if (showSettingsDrawer) {
        setShowSettingsDrawer(false);
        return true;
      }
      if (showEpisodesDrawer) {
        setShowEpisodesDrawer(false);
        return true;
      }
      if (showQualityMenu) {
        setShowQualityMenu(false);
        return true;
      }
      if (showSubtitleMenu) {
        setShowSubtitleMenu(false);
        return true;
      }
      if (showAudioMenu) {
        setShowAudioMenu(false);
        return true;
      }
      return false;
    },
  };

  const showPlayerHud = useCallback(function (
    kind: PlayerHudKind,
    text: string,
    volume?: number,
    muted?: boolean
  ) {
    setPlayerHud(function (prev) {
      var anim = prev && prev.kind === kind ? prev.anim : Date.now();
      var next: PlayerHudState = {
        kind: kind,
        text: text,
        volume: typeof volume === "number" ? volume : prev ? prev.volume : 1,
        muted: typeof muted === "boolean" ? muted : prev ? prev.muted : false,
        phase: "in",
        anim: anim,
      };
      return next;
    });
    if (hudHideTimerRef.current != null) window.clearTimeout(hudHideTimerRef.current);
    if (hudExitTimerRef.current != null) window.clearTimeout(hudExitTimerRef.current);
    hudHideTimerRef.current = window.setTimeout(function () {
      setPlayerHud(function (prev) {
        if (!prev) return null;
        var fading: PlayerHudState = {
          kind: prev.kind,
          text: prev.text,
          volume: prev.volume,
          muted: prev.muted,
          phase: "out",
          anim: prev.anim,
        };
        return fading;
      });
    }, HUD_HOLD_MS);
    hudExitTimerRef.current = window.setTimeout(function () {
      setPlayerHud(null);
    }, HUD_HOLD_MS + HUD_EXIT_MS);
  }, []);

  useEffect(function () {
    return function () {
      if (hudHideTimerRef.current != null) window.clearTimeout(hudHideTimerRef.current);
      if (hudExitTimerRef.current != null) window.clearTimeout(hudExitTimerRef.current);
      if (nextTimerRef.current != null) window.clearTimeout(nextTimerRef.current);
    };
  }, []);

  useEffect(function () {
    const host = wrapperRef.current;
    if (!host) return;
    var hideCursor = !overlayVisible && !showShortcuts && !showScoreAlert;
    if (hideCursor) host.classList.add("player-cursor-hidden");
    else host.classList.remove("player-cursor-hidden");
    return function () {
      host.classList.remove("player-cursor-hidden");
    };
  }, [overlayVisible, showScoreAlert, showShortcuts, wrapperRef]);

  useEffect(function () {
    var host = wrapperRef.current;
    if (!host) return;
    function onFocusIn(ev: FocusEvent) {
      var target = ev.target;
      if (!target || (target as Node).nodeType !== 1) return;
      var el = target as HTMLElement;
      if (!isInsidePlayerControls(el) && el.getAttribute("data-tv-play") == null) return;
      chrome.revealControlsBriefly();
    }
    host.addEventListener("focusin", onFocusIn);
    return function () {
      host.removeEventListener("focusin", onFocusIn);
    };
  }, [chrome.revealControlsBriefly, wrapperRef]);

  useEffect(function () {
    var timer = window.setTimeout(function () {
      if (document.querySelector("[data-tv-trap]")) return;
      chrome.revealControlsBriefly();
      focusPlayerPlayButton();
    }, 120);
    return function () {
      window.clearTimeout(timer);
    };
  }, [chrome.revealControlsBriefly, movieId]);

  useEffect(function () {
    const p = player;
    function applyShift() {
      var el = getVjsTextTrackDisplayEl(p);
      if (!el) return;
      var measured = 0;
      if (bottomControlsRef.current) {
        measured = bottomControlsRef.current.offsetHeight;
      }
      if (measured > 0) controlsOffsetRef.current = measured;
      var shift = subtitleUserMarginPx(subtitleMargin);
      if (overlayVisible) shift += controlsOffsetRef.current;
      setSubtitleTranslate(el, shift);
      if (overlayVisible) el.classList.add("show-bars");
      else el.classList.remove("show-bars");
    }
    applyShift();
    window.addEventListener("resize", applyShift);
    if (p && !p.isDisposed()) p.on("loadedmetadata", applyShift);
    return function () {
      window.removeEventListener("resize", applyShift);
      if (p && !p.isDisposed()) p.off("loadedmetadata", applyShift);
    };
  }, [overlayVisible, player, subtitleMargin]);

  useEffect(function () {
    var p = player;
    if (!p || p.isDisposed()) return;
    var root = typeof p.el === "function" ? p.el() : null;
    if (!root) return;
    const vid = root.querySelector("video");
    if (!(vid instanceof HTMLVideoElement)) return;
    var onEnter = function () { setPipActive(true); };
    var onLeave = function () { setPipActive(false); };
    vid.addEventListener("enterpictureinpicture", onEnter);
    vid.addEventListener("leavepictureinpicture", onLeave);
    return function () {
      vid.removeEventListener("enterpictureinpicture", onEnter);
      vid.removeEventListener("leavepictureinpicture", onLeave);
    };
  }, [player]);

  useEffect(function () {
    nextArmedRef.current = true;
    showNextRef.current = false;
    setShowNextEpisode(false);
    setNextFillOn(false);
    if (nextTimerRef.current != null) {
      window.clearTimeout(nextTimerRef.current);
      nextTimerRef.current = null;
    }
  }, [currentEpisode, currentSeason]);

  useEffect(function () {
    if (!showNextEpisode) {
      setNextFillOn(false);
      return;
    }
    var id = window.setTimeout(function () {
      setNextFillOn(true);
    }, 30);
    return function () {
      window.clearTimeout(id);
    };
  }, [showNextEpisode]);

  useEffect(function () {
    const p = player;
    if (!p || p.isDisposed()) return;
    var onTime = function () {
      if (!playerData.is_series || !nextEpisodeItem) {
        if (showNextRef.current) {
          showNextRef.current = false;
          setShowNextEpisode(false);
        }
        return;
      }
      var dur = durationOrZero(p);
      if (dur <= 0) return;
      var now = p.currentTime() || 0;
      var remaining = dur - now;
      if (remaining > NEXT_EPISODE_WINDOW_S) {
        if (nextTimerRef.current != null) {
          window.clearTimeout(nextTimerRef.current);
          nextTimerRef.current = null;
        }
        nextArmedRef.current = true;
        if (showNextRef.current) {
          showNextRef.current = false;
          setShowNextEpisode(false);
        }
        return;
      }
      if (!nextArmedRef.current || showNextRef.current) return;
      nextArmedRef.current = false;
      showNextRef.current = true;
      setShowNextEpisode(true);
      chrome.revealControlsFor(NEXT_EPISODE_VISIBLE_MS);
      if (nextTimerRef.current != null) window.clearTimeout(nextTimerRef.current);
      nextTimerRef.current = window.setTimeout(function () {
        nextTimerRef.current = null;
        showNextRef.current = false;
        setShowNextEpisode(false);
      }, NEXT_EPISODE_VISIBLE_MS);
    };
    p.on("timeupdate", onTime);
    return function () {
      p.off("timeupdate", onTime);
    };
  }, [chrome.revealControlsFor, nextEpisodeItem, player, playerData.is_series]);

  const rangeMax = chrome.safeDuration || 100;
  const rangeValue = chrome.safeDuration
    ? Math.min(chrome.current, chrome.safeDuration)
    : 0;


  async function saveWatchTimeForScore(isLast5Minutes: boolean) {
    try {
      const encrypted = await encryptAes(String(playingTime), PLAYBACK_PROGRESS_AES_KEY);
      localStorage.setItem(
        "wonmov-playing-time",
        encrypted
      );

      if (!isLast5Minutes) return;

      const response = await userRepo.saveWatchTime(encrypted);
      if (response.status && response.data && response.data.score && !blockScoreRef.current) {
        setEarnedScore(response.data.score);
        setShowScoreAlert(true);
      }

      if (response.status) {
        localStorage.removeItem("wonmov-playing-time");
      }
    } catch {}
  }

  const changeAspectRatio = () => {
    const p = player;
    if (!p || p.isDisposed()) return;

    const root = typeof p.el === "function" ? p.el() : null;
    if (!root) return;
    const videoEl = root.querySelector("video");
    if (!videoEl) return;

    var currentIndex = -1;
    var i = 0;
    for (i = 0; i < ASPECT_RATIO_OPTIONS.length; i += 1) {
      if (videoEl.classList.contains(ASPECT_RATIO_OPTIONS[i].className)) {
        currentIndex = i;
        break;
      }
    }
    var startIndex = currentIndex >= 0 ? currentIndex : 2;
    var nextAspect = ASPECT_RATIO_OPTIONS[(startIndex + 1) % ASPECT_RATIO_OPTIONS.length];

    for (i = 0; i < ASPECT_RATIO_OPTIONS.length; i += 1) {
      videoEl.classList.remove(ASPECT_RATIO_OPTIONS[i].className);
    }
    videoEl.classList.add(nextAspect.className);
    showPlayerHud("text", nextAspect.label);
  };

  const toggleControlsLock = () => {
    var nextLocked = !controlsLockedRef.current;
    controlsLockedRef.current = nextLocked;
    setControlsLocked(nextLocked);
    if (nextLocked) {
      dismissPlayerMenus();
      setShowShortcuts(false);
      showPlayerHud("text", "قفل شد");
      chrome.hideControlsNow();
    } else {
      showPlayerHud("text", "قفل باز شد");
      chrome.revealControlsBriefly();
    }
  };
  toggleLockRef.current = toggleControlsLock;
  cycleAspectRef.current = changeAspectRatio;
  seekHudRef.current = function (seconds: number) {
    showPlayerHud("time", formatPlayerTime(seconds));
  };
  volumeHudRef.current = function (volume: number, muted: boolean) {
    showPlayerHud("volume", "", volume, muted);
  };
  textHudRef.current = function (text: string) {
    showPlayerHud("text", text);
  };

  const changePlaybackSpeed = (speed: number) => {
    const p = player;
    if (p && !p.isDisposed()) {
      p.playbackRate(speed);
    }
    setPlaybackSpeed(speed);
  }

  const changeSubtitleBgColor = (color: string) => {
    const el = getVjsTextTrackDisplayEl(player);
    if (el) {
      SUBTITLE_BG_COLORS.forEach((c) => {
        el.classList.remove(c.value);
      });
      el.classList.add(color);
    }
    setSubtitleBgColor(color);
  }

  const changeSubtitleTextColor = (color: string) => {
    const el = getVjsTextTrackDisplayEl(player);
    if (el) {
      SUBTITLE_TEXT_COLORS.forEach((c) => {
        el.classList.remove(c.value);
      });
      el.classList.add(color);
    }
    setSubtitleTextColor(color);
  }

  const changeSubtitleFontWeight = (weight: string) => {
    const el = getVjsTextTrackDisplayEl(player);
    if (el) {
      SUBTITLE_FONT_WEIGHTS.forEach((w) => {
        el.classList.remove(w.value);
      });
      el.classList.add(weight);
    }
    setSubtitleFontWeight(weight);
  }

  const changeSubtitleFontFamily = (family: string) => {
    const el = getVjsTextTrackDisplayEl(player);
    if (el) {
      SUBTITLE_FONT_FAMILIES.forEach((f) => {
        el.classList.remove(f.value);
      });
      el.classList.add(family);
    }
    setSubtitleFontFamily(family);
  }

  const changeSubtitleTextSize = (size: number) => {
    const el = getVjsTextTrackDisplayEl(player);
    if (el) {
      SUBTITLE_TEXT_SIZE_VALUES.forEach(function (value) {
        el.classList.remove("text-size-" + value);
      });
      el.classList.add("text-size-" + size);
    }
    setSubtitleTextSize(size);
  }

  const changeSubtitleMargin = (margin: number) => {
    setSubtitleMargin(margin);
  }

  const persistPlayerUiSettings = () => {
    try {
      localStorage.setItem(
        PLAYER_UI_SETTINGS_STORAGE_KEY,
        JSON.stringify(playerUiPersistedSnapshot)
      );
      setNotification({ type: "success", message: "تنظیمات با موفقیت ذخیره شد" });
    } catch {
      /* کوتا یا حالت خصوصی */
    }
  };

  const resetPlayerUiSettings = () => {
    try {
      localStorage.removeItem(PLAYER_UI_SETTINGS_STORAGE_KEY);
      setNotification({ type: "success", message: "تنظیمات با موفقیت بازنشانی شد" });
    } catch {
      /* no-op */
    }
    const d = defaultPlayerUiSettings();
    setPlaybackSpeed(d.playbackSpeed);
    setSubtitleBgColor(d.subtitleBgColor);
    setSubtitleTextColor(d.subtitleTextColor);
    setSubtitleFontFamily(d.subtitleFontFamily);
    setSubtitleFontWeight(d.subtitleFontWeight);
    setSubtitleTextSize(d.subtitleTextSize);
    setSubtitleMargin(d.subtitleMargin);
  };

  const localSubFileInputRef = useRef<HTMLInputElement>(null);

  const onLocalSubtitleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext !== "srt" && ext !== "vtt") {
      setNotification({
        type: "warning",
        message: "فقط فایل‌های با پسوند .srt یا .vtt قابل قبول است.",
      });
      return;
    }
    if (file.size > LOCAL_SUBTITLE_MAX_BYTES) {
      setNotification({
        type: "warning",
        message: "حجم فایل بیش از حد مجاز است (حداکثر ۲ مگابایت).",
      });
      return;
    }
    try {
      const rawText = await file.text();
      let blob: Blob;
      if (ext === "srt") {
        const vtt = srtToWebVtt(rawText);
        if (!vtt.includes("-->")) {
          setNotification({
            type: "danger",
            message: "ساختار فایل SRT شناسایی نشد.",
          });
          return;
        }
        blob = new Blob([vtt], { type: "text/vtt;charset=utf-8" });
      } else {
        const vttBody = ensureWebVttHeader(rawText);
        if (!vttBody.includes("-->")) {
          setNotification({
            type: "danger",
            message: "فایل VTT بدون زمان‌بندی (cue) معتبر نیست.",
          });
          return;
        }
        blob = new Blob([vttBody], { type: "text/vtt;charset=utf-8" });
      }
      const url = URL.createObjectURL(blob);
      onUserSubtitleImported({
        name: userSubtitleFileLabel(file.name),
        format: "vtt",
        size: file.size,
        url,
        dubbed: false,
      });
      setNotification({ type: "success", message: "زیرنویس از فایل اضافه و فعال شد." });
    } catch {
      setNotification({ type: "danger", message: "خواندن فایل زیرنویس ناموفق بود." });
    }
  };

  var seriesPositionLabel = playerData.is_series
    ? formatSeriesPosition(currentSeason, currentEpisode)
    : "";
  var pipSupported = false;
  if (typeof document !== "undefined") {
    var pipDoc = document as Document & { pictureInPictureEnabled?: boolean };
    pipSupported = Boolean(pipDoc.pictureInPictureEnabled);
  }

  const updateTimeHover = function (el: HTMLElement, clientX: number) {
    hoverPointRef.current = { el: el, clientX: clientX };
    if (!chrome.safeDuration) {
      setTimeHover(null);
      return;
    }
    var ratio = sliderRatioFromClientX(el, clientX);
    var width = el.clientWidth;
    var seconds = ratio * chrome.safeDuration;
    var frame: SpriteFrame | null = null;
    var cue = findSpriteCue(spriteCues, seconds);
    if (cue) {
      var sheetUrl = spriteUrlForCue(cue, spriteMapRef.current);
      var sheet = sheetUrl ? spriteSheetRef.current[sheetUrl] : "fail";
      if (sheetUrl && !sheet) {
        spriteSheetRef.current[sheetUrl] = "loading";
        var cellW = cue.w;
        var cellH = cue.h;
        var img = new Image();
        img.onload = function () {
          var loadedSrc = img.getAttribute("data-sprite-proxy") === "1"
            ? proxyVideoUrl(sheetUrl)
            : sheetUrl;
          spriteSheetRef.current[sheetUrl] = {
            w: img.naturalWidth || cellW,
            h: img.naturalHeight || cellH,
            src: loadedSrc,
          };
          if (refreshHoverRef.current) refreshHoverRef.current();
        };
        img.onerror = function () {
          if (img.getAttribute("data-sprite-proxy") === "1") {
            spriteSheetRef.current[sheetUrl] = "fail";
            if (refreshHoverRef.current) refreshHoverRef.current();
            return;
          }
          img.setAttribute("data-sprite-proxy", "1");
          img.src = proxyVideoUrl(sheetUrl);
        };
        img.src = sheetUrl;
      }
      var loaded = sheetUrl ? spriteSheetRef.current[sheetUrl] : "fail";
      if (loaded && loaded !== "loading" && loaded !== "fail") {
        var display = spriteDisplaySize(cue.w, cue.h, width);
        if (display.w > 0 && display.h > 0 && loaded.w > 0 && loaded.h > 0) {
          frame = {
            url: loaded.src,
            dispW: display.w,
            dispH: display.h,
            imgW: Math.round(loaded.w * display.scale),
            imgH: Math.round(loaded.h * display.scale),
            offsetX: Math.round(-cue.x * display.scale),
            offsetY: Math.round(-cue.y * display.scale),
          };
        }
      }
    }
    var labelHalf = 36;
    if (frame && frame.dispW / 2 > labelHalf) labelHalf = frame.dispW / 2;
    var x = ratio * width;
    if (x < labelHalf) x = labelHalf;
    if (width > labelHalf * 2 && x > width - labelHalf) x = width - labelHalf;
    setTimeHover({
      left: x,
      text: formatPlayerTime(seconds),
      frame: frame,
    });
  };
  refreshHoverRef.current = function () {
    var point = hoverPointRef.current;
    if (!point) return;
    updateTimeHover(point.el, point.clientX);
  };

  return (
    <React.Fragment>
    {playerHud ? (
      <div
        key={playerHud.kind + "-" + String(playerHud.anim)}
        className={"player-hud " + (playerHud.phase === "out" ? "player-hud--out" : "player-hud--in")}
      >
        <div className="player-hud__badge" dir="rtl">
          {playerHud.kind === "volume" ? (
            <img
              src={volumeIconForLevel(playerHud.muted, playerHud.volume)}
              alt=""
              width={28}
              height={28}
            />
          ) : (
            <span>{playerHud.text}</span>
          )}
        </div>
      </div>
    ) : null}
    {controlsLocked ? (
      <button
        type="button"
        className="player-lock-unlock"
        aria-label="باز کردن قفل"
        onClick={toggleControlsLock}
      >
        <img src={lockIcon} alt="" width={28} height={28} />
      </button>
    ) : null}
    <div
      data-player-chrome=""
      className={`player-chrome absolute inset-0 z-20 flex flex-col justify-between bg-linear-to-t from-black/85 via-transparent to-transparent p-4 transition-opacity duration-300 touch-manipulation ${
        overlayVisible ? "player-chrome--visible" : "player-chrome--hidden"
      } ${chrome.fullscreen ? "!p-6" : ""}`}
    >
      {/* ردیف بالا */}
      <div
        data-player-controls-zone=""
        className={`top-player-controls ${chromeControlPointer} flex items-center justify-between text-sm font-medium`}
      >
        <div className="flex md:flex-1 justify-start">
          <button
            type="button"
            className="player-top-btn"
            aria-label="بازگشت"
            onClick={onBack}
          >
            <img src={backIcon} alt="" width={ICON_PX} height={ICON_PX} />
          </button>
        </div>
        <div className="flex flex-1 justify-center">
          <p className="text-sm font-medium text-white flex items-center">
            <span className="mt-1">{playerData.name}</span>
            {seriesPositionLabel ? (
              <span className="player-title__meta" dir="rtl">{seriesPositionLabel}</span>
            ) : null}
            <span className="text-xs bg-lime-400 text-black px-1 rounded-full ml-2">
              {currentQualityCode}
            </span>
          </p>
        </div>
        <div className="flex md:flex-1 gap-6 justify-end">
          <button
            type="button"
            className="player-top-btn"
            aria-label="تنظیمات"
            onClick={function () {
              chrome.revealControlsBriefly();
              setShowQualityMenu(false);
              setShowSubtitleMenu(false);
              setShowAudioMenu(false);
              setShowEpisodesDrawer(false);
              setShowSettingsDrawer(function (prev) {
                if (!prev) {
                  var p = player;
                  if (p && !p.isDisposed()) {
                    void p.pause();
                  }
                }
                return !prev;
              });
            }}
          >
            <img src={settingsIcon} alt="" width={ICON_PX} height={ICON_PX} />
          </button>
          {playerData.is_series && (
            <button
              type="button"
              className="player-top-btn"
              aria-label="قسمت‌ها"
              onClick={function () {
                chrome.revealControlsBriefly();
                setShowQualityMenu(false);
                setShowSubtitleMenu(false);
                setShowAudioMenu(false);
                setShowSettingsDrawer(false);
                setExpandedSeasons(function (prev) {
                  return prev.indexOf(currentSeason) >= 0 ? prev : prev.concat([currentSeason]);
                });
                setShowEpisodesDrawer(function (prev) {
                  if (!prev) {
                    var p = player;
                    if (p && !p.isDisposed()) {
                      void p.pause();
                    }
                  }
                  return !prev;
                });
              }}
            >
              <img src={episodeIcon} alt="" width={ICON_PX} height={ICON_PX} />
            </button>
          )}
        </div>
      </div>

      {/* اسپینر لودینگ وسط صفحه هنگام بافرینگ ویدیو */}
      {chrome.buffering && (
        <div className="player-loading" data-player-controls-zone="">
          <div className="player-loading__inner">
            <img src={loadingGif} alt="در حال بارگذاری" />
          </div>
        </div>
      )}

      {/* دکمهٔ بزرگ وسط — فقط در حالت توقف و وقتی بافرینگ نیست */}
      {chrome.paused && !chrome.buffering && (
        <div
          data-player-controls-zone=""
          className={`center-player-control ${chromeControlPointer}`}
        >
          <button
            type="button"
            className="center-player-control__btn"
            onClick={function () {
              var p = player;
              if (!p || p.isDisposed()) return;
              void p.play();
            }}
          >
            <img src={playIcon} alt="play" width={ICON_PX} height={ICON_PX} />
          </button>
        </div>
      )}

      {/* پایین: زمان، اسلایدر، دکمه‌ها */}
      <div
        ref={bottomControlsRef}
        data-player-controls-zone=""
        className={"player-controls " + chromeControlPointer}
      >
        {showNextEpisode && nextEpisodeItem ? (
          <button
            type="button"
            className={"player-next-episode" + (nextFillOn ? " player-next-episode--run" : "")}
            dir="rtl"
            onClick={function () {
              onEpisodeSelect(nextEpisodeItem.season, nextEpisodeItem.episode);
            }}
          >
            <span className="player-next-episode__fill" />
            <span className="player-next-episode__label">قسمت بعد</span>
          </button>
        ) : null}
        <div className="player-controls__time-row">
          <span className="player-controls__time-cell">
            {formatPlayerTime(chrome.current)}
          </span>

          <div className="player-controls__time-cell player-controls__time-cell--slider">
            <div
              className="player-controls__slider player-controls__slider--time"
              onMouseMove={function (e) {
                updateTimeHover(e.currentTarget, e.clientX);
              }}
              onMouseLeave={function () {
                hoverPointRef.current = null;
                setTimeHover(null);
              }}
              onTouchMove={function (e) {
                var cx = clientXFromTouch(e);
                if (cx == null) return;
                updateTimeHover(e.currentTarget, cx);
              }}
              onTouchEnd={function () {
                hoverPointRef.current = null;
                setTimeHover(null);
              }}
            >
              {timeHover ? (
                <div
                  className="player-controls__time-hover"
                  style={{ left: timeHover.left + "px" }}
                >
                  {timeHover.frame ? (
                    <div
                      className="player-controls__time-preview"
                      style={{
                        width: timeHover.frame.dispW,
                        height: timeHover.frame.dispH,
                      }}
                    >
                      <img
                        src={timeHover.frame.url}
                        alt=""
                        style={{
                          width: timeHover.frame.imgW,
                          height: timeHover.frame.imgH,
                          marginLeft: timeHover.frame.offsetX,
                          marginTop: timeHover.frame.offsetY,
                        }}
                      />
                    </div>
                  ) : null}
                  <span className="player-controls__time-hover-label">{timeHover.text}</span>
                </div>
              ) : null}
              <input
                data-player-time-slider=""
                aria-label="زمان پخش"
                className="player-controls__slider-input"
                disabled={!chrome.safeDuration}
                max={rangeMax}
                min={0}
                step={0.05}
                type="range"
                value={rangeValue}
                onChange={(e) => {
                  const p = player;
                  if (!p || p.isDisposed()) return;
                  p.currentTime(Number(e.target.value));
                }}
                onMouseDown={function (e) {
                  const p = player;
                  if (!p || p.isDisposed() || !chrome.safeDuration) return;
                  chrome.scrubWasPlayingRef.current = !p.paused();
                  void p.pause();
                  const ratio = sliderRatioFromClientX(e.currentTarget, e.clientX);
                  p.currentTime(ratio * chrome.safeDuration);
                }}
                onTouchStart={function (e) {
                  const p = player;
                  if (!p || p.isDisposed() || !chrome.safeDuration) return;
                  chrome.scrubWasPlayingRef.current = !p.paused();
                  void p.pause();
                  const cx = clientXFromTouch(e);
                  if (cx != null) {
                    const ratio = sliderRatioFromClientX(e.currentTarget, cx);
                    p.currentTime(ratio * chrome.safeDuration);
                  }
                }}
                onMouseUp={function () {
                  const p = player;
                  if (!p || p.isDisposed()) return;
                  if (chrome.scrubWasPlayingRef.current) {
                    void p.play();
                  }
                }}
                onTouchEnd={function () {
                  const p = player;
                  if (!p || p.isDisposed()) return;
                  if (chrome.scrubWasPlayingRef.current) {
                    void p.play();
                  }
                }}
              />
              <div className="player-controls__slider-track">
                <div
                  className="player-controls__slider-fill"
                  style={{ width: chrome.timeRailPercent + "%" }}
                />
              </div>
              <div
                className="player-controls__slider-thumb player-controls__slider-thumb--visible"
                style={sliderThumbStyle(chrome.timeRailPercent, 6)}
              />
            </div>
          </div>

          <span className="player-controls__time-cell">
            {formatPlayerTime(chrome.safeDuration)}
          </span>
        </div>

        <div className="player-controls__actions-row">
          <div className="player-controls__actions-cell player-controls__actions-cell--left">
            <div className="player-controls__icon-group">
              <PlayerControlTooltip label="میانبرها" className="player-tooltip--md-only">
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label="میانبرها"
                  onClick={function () {
                    setShowShortcuts(true);
                  }}
                >
                  <svg
                    className="player-controls__icon"
                    width={ICON_PX}
                    height={ICON_PX}
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <rect x="3" y="7" width="22" height="14" rx="2" stroke="#D4D4D8" strokeWidth="1.5" />
                    <path d="M7 12h2M12 12h2M17 12h2M7 16h4M13 16h8" stroke="#D4D4D8" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </PlayerControlTooltip>
              <PlayerControlTooltip label="قفل کنترل‌ها" className="player-tooltip--desktop-only">
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label="قفل کنترل‌ها"
                  aria-pressed={controlsLocked}
                  onClick={toggleControlsLock}
                >
                  <img
                    src={lockIcon}
                    alt=""
                    width={ICON_PX}
                    height={ICON_PX}
                    className="player-controls__icon"
                  />
                </button>
              </PlayerControlTooltip>
              <PlayerControlTooltip label={chrome.fullscreen ? "خروج از تمام‌صفحه" : "تمام‌صفحه"}>
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label={chrome.fullscreen ? "خروج از تمام‌صفحه" : "تمام‌صفحه"}
                  onClick={function () {
                    var shell = wrapperRef.current;
                    if (!shell) return;
                    void (document.fullscreenElement
                      ? document.exitFullscreen()
                      : shell.requestFullscreen());
                  }}
                >
                  <img
                    src={chrome.fullscreen ? fullscreenExitIcon : fullscreenIcon}
                    alt=""
                    width={ICON_PX}
                    height={ICON_PX}
                    className="player-controls__icon"
                  />
                </button>
              </PlayerControlTooltip>
              <div className="player-controls__volume-wrap">
                <PlayerControlTooltip label={chrome.muted || chrome.volume === 0 ? "وصل صدا" : "قطع صدا"}>
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label={chrome.muted || chrome.volume === 0 ? "وصل صدا" : "قطع صدا"}
                  onClick={() => {
                    const p = player;
                    if (!p || p.isDisposed()) return;
                    p.muted(!p.muted());
                  }}
                >
                  <img
                    src={
                      chrome.muted || chrome.volume === 0
                        ? volumeCrossIcon
                        : chrome.volume > 0.5
                          ? volumeLoudIcon
                          : chrome.volume > 0.25
                            ? volumeSmallIcon
                            : volumeIcon
                    }
                    alt="volume"
                    width={ICON_PX}
                    height={ICON_PX}
                    className="player-controls__icon"
                  />
                </button>
                </PlayerControlTooltip>
                <div className="player-controls__volume-slider">
                  <div className="player-controls__slider">
                    <input
                      aria-label="صدا"
                      className="player-controls__slider-input"
                      max={1}
                      min={0}
                      step={0.03}
                      type="range"
                      value={chrome.muted ? 0 : chrome.volume}
                      onChange={(e) => {
                        const p = player;
                        if (!p || p.isDisposed()) return;
                        const v = Number(e.target.value);
                        p.volume(v);
                        if (v > 0 && p.muted()) {
                          p.muted(false);
                        }
                      }}
                      onMouseDown={(e) => {
                        const p = player;
                        if (!p || p.isDisposed()) return;
                        const v = sliderRatioFromClientX(e.currentTarget, e.clientX);
                        p.volume(v);
                        if (v > 0 && p.muted()) {
                          p.muted(false);
                        }
                      }}
                      onTouchStart={(e) => {
                        const p = player;
                        if (!p || p.isDisposed()) return;
                        const cx = clientXFromTouch(e);
                        if (cx == null) return;
                        const v = sliderRatioFromClientX(e.currentTarget, cx);
                        p.volume(v);
                        if (v > 0 && p.muted()) {
                          p.muted(false);
                        }
                      }}
                    />
                    <div className="player-controls__slider-track">
                      <div
                        className="player-controls__slider-fill"
                        style={{ width: chrome.volRailPercent + "%" }}
                      />
                    </div>
                    <div
                      className="player-controls__slider-thumb player-controls__slider-thumb--visible"
                      style={sliderThumbStyle(chrome.volRailPercent, 6)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="player-controls__actions-cell player-controls__actions-cell--center">
            <div className="player-controls__icon-group">
              <PlayerControlTooltip label="۱۰ ثانیه عقب">
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label="۱۰ ثانیه عقب"
                  onClick={() => {
                    const p = player;
                    if (!p || p.isDisposed()) return;
                    const t = p.currentTime() || 0;
                    p.currentTime(Math.max(0, t - 10));
                  }}
                >
                  <img src={redoIcon} alt="" width={ICON_PX} height={ICON_PX} className="player-controls__icon" />
                </button>
              </PlayerControlTooltip>
              <PlayerControlTooltip label={chrome.paused ? "پخش" : "توقف"}>
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  data-tv-play=""
                  data-tv-initial=""
                  aria-label={chrome.paused ? "پخش" : "توقف"}
                  onClick={function () {
                    var p = player;
                    if (!p || p.isDisposed()) return;
                    void (p.paused() ? p.play() : p.pause());
                  }}
                >
                  <img
                    src={chrome.paused ? playIcon : pauseIcon}
                    alt=""
                    width={ICON_PX}
                    height={ICON_PX}
                    className="player-controls__icon"
                  />
                </button>
              </PlayerControlTooltip>
              <PlayerControlTooltip label="۱۰ ثانیه جلو">
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label="۱۰ ثانیه جلو"
                  onClick={() => {
                    const p = player;
                    if (!p || p.isDisposed()) return;
                    const t = p.currentTime() || 0;
                    const du = p.duration();
                    const end =
                      typeof du === "number" && Number.isFinite(du) ? du : t + 10;
                    p.currentTime(Math.min(end, t + 10));
                  }}
                >
                  <img src={forwardIcon} alt="" width={ICON_PX} height={ICON_PX} className="player-controls__icon" />
                </button>
              </PlayerControlTooltip>
            </div>
          </div>

          <div className="player-controls__actions-cell player-controls__actions-cell--right">
            <div className="player-controls__icon-group">
              {subtitlesForTracks.length > 0 && (
                <div className="player-controls__menu-anchor">
                  <PlayerControlTooltip label="زیرنویس" hidden={showSubtitleMenu}>
                    <button
                      type="button"
                      className="player-controls__icon-btn"
                      aria-label="زیرنویس"
                      onClick={() => {
                        chrome.revealControlsBriefly();
                        setShowAudioMenu(false);
                        setShowQualityMenu(false);
                        setShowSubtitleMenu(!showSubtitleMenu);
                      }}
                    >
                  <svg
                    width={ICON_PX}
                    height={ICON_PX}
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="player-controls__icon"
                  >
                    <path
                      d="M12.4539 11.7428C12.1891 11.5396 11.8919 11.3767 11.5712 11.2634C11.2365 11.1451 10.8764 11.0807 10.5013 11.0807C8.72866 11.0807 7.29165 12.5177 7.29165 14.2904C7.29165 16.063 8.72866 17.5 10.5013 17.5C10.8764 17.5 11.2365 17.4357 11.5712 17.3174C11.8919 17.2041 12.1891 17.0412 12.4539 16.838M20.7138 11.7428C20.4491 11.5396 20.1518 11.3767 19.8311 11.2634C19.4965 11.1451 19.1364 11.0807 18.7612 11.0807C16.9886 11.0807 15.5516 12.5177 15.5516 14.2904C15.5516 16.063 16.9886 17.5 18.7612 17.5C19.1364 17.5 19.4965 17.4357 19.8311 17.3174C20.1518 17.2041 20.4491 17.0412 20.7138 16.838M7.93331 22.1667H20.0666C22.0268 22.1667 23.0069 22.1667 23.7556 21.7852C24.4142 21.4497 24.9496 20.9142 25.2852 20.2557C25.6666 19.507 25.6666 18.5269 25.6666 16.5667V11.4334C25.6666 9.47319 25.6666 8.4931 25.2852 7.74441C24.9496 7.08584 24.4142 6.55041 23.7556 6.21485C23.0069 5.83337 22.0268 5.83337 20.0666 5.83337H7.93331C5.97313 5.83337 4.99304 5.83337 4.24435 6.21485C3.58578 6.55041 3.05035 7.08584 2.71479 7.74441C2.33331 8.4931 2.33331 9.47319 2.33331 11.4334V16.5667C2.33331 18.5269 2.33331 19.507 2.71479 20.2557C3.05035 20.9142 3.58578 21.4497 4.24435 21.7852C4.99304 22.1667 5.97313 22.1667 7.93331 22.1667Z"
                      stroke={captionValue === "off" ? "#D4D4D8" : "#9ae600"}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                    </button>
                  </PlayerControlTooltip>
                  {showSubtitleMenu && (
                    <div className="player-controls__menu player-controls__menu--center" data-tv-trap="">
                      <ul className="player-controls__menu-list">
                        {captionItems.map(function (sub) {
                          var isActive = captionValue === sub.value;
                          return (
                          <li key={sub.value}>
                            <button
                              type="button"
                              className={
                                "player-controls__menu-item" +
                                (isActive ? " player-controls__menu-item--active" : "")
                              }
                              data-tv-initial={isActive ? "" : undefined}
                              onClick={function () {
                                var v = sub.value;
                                setCaptionValue(v);
                                var p = player;
                                if (!p || p.isDisposed()) return;
                                applyCaptionSelection(p, v);
                                setShowSubtitleMenu(false);
                              }}
                            >
                              {sub.label}
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {canToggleDubSubtitle && (
                <PlayerControlTooltip
                  label={
                    playbackStreamType === "farsi_dubbed"
                      ? "تغییر به نسخه با زیرنویس"
                      : "تغییر به دوبله فارسی"
                  }
                >
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label={
                    playbackStreamType === "farsi_dubbed"
                      ? "تغییر به نسخه با زیرنویس"
                      : "تغییر به دوبله فارسی"
                  }
                  onClick={() => {
                    setShowSubtitleMenu(false);
                    setShowQualityMenu(false);
                    setShowAudioMenu(false);
                    onToggleDubSubtitle();
                  }}
                >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={ICON_PX}
                  height={ICON_PX}
                  viewBox="0 0 20 20"
                  fill="none"
                  className="player-controls__icon"
                  aria-hidden="true"
                >
                  <path
                    d="M6.66663 17.5H13.3333"
                    stroke={playbackStreamType === "farsi_dubbed" ? "#9ae600" : "#d4d4d8"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 15V17.5"
                    stroke={playbackStreamType === "farsi_dubbed" ? "#9ae600" : "#d4d4d8"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.3333 5.35714C13.3333 3.77919 11.8409 2.5 9.99996 2.5C8.15901 2.5 6.66663 3.77919 6.66663 5.35714V9.64286C6.66663 11.2208 8.15901 12.5 9.99996 12.5C11.8409 12.5 13.3333 11.2208 13.3333 9.64286V5.35714Z"
                    stroke={playbackStreamType === "farsi_dubbed" ? "#9ae600" : "#d4d4d8"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.16663 9.16666C4.16663 12.3883 6.77829 15 9.99996 15C13.2216 15 15.8333 12.3883 15.8333 9.16666"
                    stroke={playbackStreamType === "farsi_dubbed" ? "#9ae600" : "#d4d4d8"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                </button>
                </PlayerControlTooltip>
              )}

              <PlayerControlTooltip label="نسبت تصویر" className="player-tooltip--desktop-only">
                <button
                  type="button"
                  className="player-controls__icon-btn"
                  aria-label="نسبت تصویر"
                  onClick={changeAspectRatio}
                >
                  <img
                    src={aspectRatioIcon}
                    alt=""
                    width={ICON_PX}
                    height={ICON_PX}
                    className="player-controls__icon"
                  />
                </button>
              </PlayerControlTooltip>

              {playbackOptions.length > 1 && (
                <div className="player-controls__menu-anchor">
                  <PlayerControlTooltip label="کیفیت" hidden={showQualityMenu}>
                    <button
                      type="button"
                      className="player-controls__icon-btn"
                      aria-label="کیفیت"
                      onClick={() => {
                        chrome.revealControlsBriefly();
                        setShowSubtitleMenu(false);
                        setShowAudioMenu(false);
                        setShowQualityMenu(!showQualityMenu);
                      }}
                    >
                      <img
                        src={qualityIcon}
                        alt=""
                        width={ICON_PX}
                        height={ICON_PX}
                        className="player-controls__icon"
                      />
                    </button>
                  </PlayerControlTooltip>
                  {showQualityMenu && (
                    <div className="player-controls__menu" data-tv-trap="">
                      <ul className="player-controls__menu-list">
                        {playbackOptions.map(function (opt) {
                          var isActive = streamUrl === opt.url;
                          return (
                          <li key={opt.url}>
                            <button
                              type="button"
                              className={
                                "player-controls__menu-item" +
                                (isActive ? " player-controls__menu-item--active" : "")
                              }
                              data-tv-initial={isActive ? "" : undefined}
                              onClick={function () {
                                onStreamChange(opt.url);
                                setShowQualityMenu(false);
                              }}
                            >
                              {opt.quality}
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {pipSupported ? (
                <PlayerControlTooltip label="تصویر در تصویر">
                  <button
                    type="button"
                    className="player-controls__icon-btn"
                    aria-label="تصویر در تصویر"
                    onClick={function () {
                      var p = player;
                      if (!p || p.isDisposed()) return;
                      chrome.revealControlsBriefly();
                      void togglePictureInPicture(p);
                    }}
                  >
                    <svg
                      className="player-controls__icon"
                      width={ICON_PX}
                      height={ICON_PX}
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <rect
                        x="3"
                        y="6"
                        width="22"
                        height="16"
                        rx="2"
                        stroke={pipActive ? "#9ae600" : "#D4D4D8"}
                        strokeWidth="1.5"
                      />
                      <rect
                        x="14"
                        y="13"
                        width="9"
                        height="7"
                        rx="1"
                        fill={pipActive ? "#9ae600" : "#D4D4D8"}
                      />
                    </svg>
                  </button>
                </PlayerControlTooltip>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {showSettingsDrawer && (
        <>
          <div
            className="player-settings-drawer__overlay"
            data-player-ignore-toggle=""
            onClick={() => setShowSettingsDrawer(false)}
          />
          <aside className="player-settings-drawer" data-tv-trap="">
            <div className="player-settings-drawer__scroll custom-scrollbar">
              <div className="player-settings-drawer__header">
                <div className="player-settings-drawer__header-close">
                  <button
                    type="button"
                    className="player-settings-drawer__close"
                    data-tv-close=""
                    aria-label="بستن"
                    onClick={function () {
                      setShowSettingsDrawer(false);
                    }}
                  >
                    <img src={closeIcon} alt="" width={24} height={24} />
                  </button>
                </div>
                <div className="player-settings-drawer__header-title">
                  <div className="player-settings-drawer__title-wrap">
                    <h3 className="player-settings-drawer__title">تنظیمات</h3>
                    <img
                      src={settingsIcon}
                      alt="settings"
                      width={ICON_PX}
                      height={ICON_PX}
                      className="player-settings-drawer__title-icon"
                    />
                  </div>
                </div>
              </div>

              <div className="player-settings-drawer__body">
                <section className="player-settings__section">
                  <div className="player-settings__section-head">
                    <img src={speedIcon} alt="speed" width={ICON_PX} height={ICON_PX} className="player-settings__section-icon" />
                    <h4 className="player-settings__section-title">سرعت پخش</h4>
                  </div>
                  <div className="player-settings__options">
                    {RATE_KEYBOARD_STEPS.map((rate) => (
                      <span key={rate.toString()} className="player-settings__option-wrap">
                        <button
                          type="button"
                          className={
                            "player-settings__option" +
                            (rate === playbackSpeed ? " player-settings__option--active" : "")
                          }
                          onClick={() => changePlaybackSpeed(rate)}
                        >
                          {rate}x
                        </button>
                      </span>
                    ))}
                  </div>
                </section>

                <section className="player-settings__section">
                  <div className="player-settings__section-head">
                    <img src={bgColorIcon} alt="bg color" width={ICON_PX} height={ICON_PX} className="player-settings__section-icon" />
                    <h4 className="player-settings__section-title">رنگ پس زمینه زیرنویس</h4>
                  </div>
                  <div className="player-settings__options">
                    {SUBTITLE_BG_COLORS.map((item) => (
                      <span key={item.value} className="player-settings__option-wrap player-settings__option-wrap--third">
                        <button
                          type="button"
                          className={
                            "player-settings__option" +
                            (item.value === subtitleBgColor ? " player-settings__option--active" : "")
                          }
                          onClick={() => changeSubtitleBgColor(item.value)}
                        >
                          {item.label}
                        </button>
                      </span>
                    ))}
                  </div>
                </section>

                <section className="player-settings__section">
                  <div className="player-settings__section-head">
                    <img src={colorIcon} alt="color" width={ICON_PX} height={ICON_PX} className="player-settings__section-icon" />
                    <h4 className="player-settings__section-title">رنگ زیرنویس</h4>
                  </div>
                  <div className="player-settings__options">
                    {SUBTITLE_TEXT_COLORS.map((item) => (
                      <span key={item.value} className="player-settings__option-wrap player-settings__option-wrap--third">
                        <button
                          type="button"
                          className={
                            "player-settings__option" +
                            (item.value === subtitleTextColor ? " player-settings__option--active" : "")
                          }
                          onClick={() => changeSubtitleTextColor(item.value)}
                        >
                          {item.label}
                        </button>
                      </span>
                    ))}
                  </div>
                </section>

                <section className="player-settings__section">
                  <div className="player-settings__section-head">
                    <img src={fontIcon} alt="font" width={ICON_PX} height={ICON_PX} className="player-settings__section-icon" />
                    <h4 className="player-settings__section-title">فونت</h4>
                  </div>
                  <div className="player-settings__options">
                    {SUBTITLE_FONT_FAMILIES.map((item) => (
                      <span key={item.value} className="player-settings__option-wrap player-settings__option-wrap--third">
                        <button
                          type="button"
                          className={
                            "player-settings__option" +
                            (item.value === subtitleFontFamily ? " player-settings__option--active" : "")
                          }
                          onClick={() => changeSubtitleFontFamily(item.value)}
                        >
                          {item.label}
                        </button>
                      </span>
                    ))}
                  </div>
                </section>

                <section className="player-settings__section">
                  <div className="player-settings__section-head">
                    <img src={fontIcon} alt="font" width={ICON_PX} height={ICON_PX} className="player-settings__section-icon" />
                    <h4 className="player-settings__section-title">وزن فونت</h4>
                  </div>
                  <div className="player-settings__options">
                    {SUBTITLE_FONT_WEIGHTS.map((item) => (
                      <span key={item.value} className="player-settings__option-wrap player-settings__option-wrap--third">
                        <button
                          type="button"
                          className={
                            "player-settings__option" +
                            (item.value === subtitleFontWeight ? " player-settings__option--active" : "")
                          }
                          onClick={() => changeSubtitleFontWeight(item.value)}
                        >
                          {item.label}
                        </button>
                      </span>
                    ))}
                  </div>
                </section>

                <section className="player-settings__section">
                  <div className="player-settings__section-head">
                    <img src={fontIcon} alt="font size" width={ICON_PX} height={ICON_PX} className="player-settings__section-icon" />
                    <h4 className="player-settings__section-title">اندازه متن</h4>
                  </div>
                  <div className="player-settings__options">
                    {SUBTITLE_TEXT_SIZES.map(function (item) {
                      return (
                        <span key={item.value} className="player-settings__option-wrap player-settings__option-wrap--third">
                          <button
                            type="button"
                            className={
                              "player-settings__option" +
                              (item.value === subtitleTextSize ? " player-settings__option--active" : "")
                            }
                            onClick={function () {
                              changeSubtitleTextSize(item.value);
                            }}
                          >
                            {item.label}
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </section>

                <section className="player-settings__section">
                  <div className="player-settings__section-head">
                    <img src={rulerIcon} alt="ruler" width={ICON_PX} height={ICON_PX} className="player-settings__section-icon" />
                    <h4 className="player-settings__section-title">فاصله زیرنویس از پایین</h4>
                  </div>
                  <div className="player-settings__options">
                    {SUBTITLE_MARGINS.map(function (item) {
                      return (
                        <span key={item.value} className="player-settings__option-wrap player-settings__option-wrap--third">
                          <button
                            type="button"
                            className={
                              "player-settings__option" +
                              (item.value === subtitleMargin ? " player-settings__option--active" : "")
                            }
                            onClick={function () {
                              changeSubtitleMargin(item.value);
                            }}
                          >
                            {item.label}
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </section>

                <input
                  ref={localSubFileInputRef}
                  type="file"
                  accept=".srt,.vtt,text/vtt,application/x-subrip"
                  className="player-settings__file-input"
                  onChange={onLocalSubtitleFileChange}
                />
                <button
                  type="button"
                  className="player-settings__file-btn"
                  onClick={() => localSubFileInputRef.current && localSubFileInputRef.current.click()}
                >
                  انتخاب زیرنویس
                </button>

                <div className="player-settings__footer">
                  <div className="player-settings__footer-col">
                    <button
                      type="button"
                      className="player-settings__footer-btn player-settings__footer-btn--save"
                      onClick={persistPlayerUiSettings}
                    >
                      ذخیره تنظیمات
                    </button>
                  </div>
                  <div className="player-settings__footer-col">
                    <button
                      type="button"
                      className="player-settings__footer-btn player-settings__footer-btn--reset"
                      onClick={resetPlayerUiSettings}
                    >
                      بازنشانی تنظیمات
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </>
      )}

      {showEpisodesDrawer && (
        <>
          <div
            className="player-episodes-drawer__overlay"
            data-player-ignore-toggle=""
            onClick={() => setShowEpisodesDrawer(false)}
          />
          <aside className="player-episodes-drawer" data-tv-trap="">
            <div className="player-episodes-drawer__scroll custom-scrollbar">
              <div className="player-episodes-drawer__header">
                <div className="player-episodes-drawer__header-close">
                  <button
                    type="button"
                    className="player-episodes-drawer__close"
                    data-tv-close=""
                    aria-label="بستن"
                    onClick={function () {
                      setShowEpisodesDrawer(false);
                    }}
                  >
                    <img src={closeIcon} alt="" width={24} height={24} />
                  </button>
                </div>
                <div className="player-episodes-drawer__header-title">
                  <div className="player-episodes-drawer__title-wrap">
                    <h3 className="player-episodes-drawer__title">انتخاب قسمت</h3>
                    <img
                      src={episodeIcon}
                      alt="episode panel"
                      width={ICON_PX}
                      height={ICON_PX}
                      className="player-episodes-drawer__title-icon"
                    />
                  </div>
                </div>
              </div>

              <div className="player-episodes-drawer__body">
                {seasons.map((seasonNo) => {
                  const isExpanded = expandedSeasons.includes(seasonNo);
                  const seasonEpisodes = episodesBySeason.get(seasonNo) ?? [];
                  return (
                    <section key={"season-" + seasonNo} className="player-episodes__season">
                      <button
                        type="button"
                        className="player-episodes__season-trigger"
                        onClick={() => {
                          setExpandedSeasons((prev) =>
                            prev.includes(seasonNo)
                              ? prev.filter((s) => s !== seasonNo)
                              : [...prev, seasonNo]
                          );
                        }}
                      >
                        <span
                          className={
                            "player-episodes__season-chevron" +
                            (isExpanded ? " player-episodes__season-chevron--expanded" : "")
                          }
                        >
                          <img
                            src={chevronDownIcon}
                            alt={isExpanded ? "بستن فصل" : "باز کردن فصل"}
                            width={16}
                            height={16}
                          />
                        </span>
                        <span className="player-episodes__season-title">
                          فصل {seasonNo}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="player-episodes__episode-list">
                          {seasonEpisodes.map((ep) => {
                            const isActive =
                              ep.season === currentSeason && ep.episode === currentEpisode;
                            return (
                              <button
                                key={ep.season + "-" + ep.episode}
                                type="button"
                                className={
                                  "player-episodes__episode-btn" +
                                  (isActive ? " player-episodes__episode-btn--active" : "")
                                }
                                onClick={() => {
                                  onEpisodeSelect(ep.season, ep.episode);
                                  setShowEpisodesDrawer(false);
                                }}
                              >
                                <span className="player-episodes__episode-icon">
                                  {isActive ? (
                                    <img src={eyeIcon} alt="قسمت فعال" width={20} height={20} />
                                  ) : null}
                                </span>
                                <span className="player-episodes__episode-label">
                                  {ep.name || "قسمت " + ep.episode}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
          </aside>
        </>
      )}

      {showResumeDialog &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <PlayerResumeDialog
            watchedTimeSeconds={playerData.watched_time}
            onResume={handleResumeWatching}
            onStartOver={handleStartWatchingFromBeginning}
          />,
          document.body
        )}

    </div>
    {showShortcuts ? <PlayerShortcutsDialog onClose={function () { setShowShortcuts(false); }} /> : null}
    {showScoreAlert ? (
      <PlayerScoreAlert
        score={earnedScore}
        onClose={function () { setShowScoreAlert(false); }}
      />
    ) : null}
    </React.Fragment>
  );
}
